/**
 * Planning service — read/write for PlanCycle, PlanVersion, PlanOfRecord (canon 10–17).
 * Build contract: DB is source of truth; no localStorage persistence.
 *
 * BOUNDARY: Prioritization outputs are immutable inputs to planning.
 * - Planning MAY: read prioritization artifacts (items, item_pools, priority_sets); create plan_cycles,
 *   plan_versions, initiatives, work_units, and evaluation evidence (constraint evaluations, POR snapshots).
 * - Planning MUST NOT: modify prioritization items, modify ranking outputs (item_pools.priority_ids, etc.),
 *   or mutate any prioritization state. All writes from this module are to planning-owned tables only.
 */

import { fetchPublishedTrackResults } from './tracks/publishedTrackResult';
import { getSupabaseClient } from './supabaseClient';
import { fetchHeldValues } from './itemCycleStandingService';
import { resolveRecipeIdForWorkType } from './executionRecipeService';
import { getCurrentWorkspaceId } from './supabaseSync';
import { getTenantContext } from './tenantContext';
import {
  getWorkUnitReadiness,
  getCriticalPathWorkUnitIds,
  getNextRightWorkUnits,
  porAuthorizable,
  bucketsMaterialized,
  evaluationComputed,
  type PorAuthorizableResult,
} from './planningQueries';
import type { PorBlocker } from './planningQueries';
import {
  sortPlanCyclesForDisplay,
  type PlanCycle,
  type PlanVersion,
  type PlanOfRecord,
  type PlanVersionChangeReason,
  type CreatePlanCycleInput,
  type PlanOfRecordSnapshot,
  type PlanOfRecordSnapshotPayload,
  type ActivePlanOfRecord,
  type PlanningInputSnapshot,
  type Phase1PlanningDto,
} from '../types/planning';
import type {
  Initiative,
  WorkUnit,
  DependencyLink,
  CapacityAllocation,
  Milestone,
  Assumption,
  Risk,
  PlanConstraintEvaluation,
  RankSequenceDivergence,
  TradeoffDecision,
} from '../types/planning';
import type { DependencyTypeEnum, InitiativeDropReason, WorkUnitReadinessState } from '../types/planning';
import type {
  PlanCycleContext,
  PlanCycleWithContext,
  CertaintyLevel,
  InitiativeWithCertainty,
  PlanOpenQuestion,
  OpenQuestionStatus,
  PorDimensions,
  PlanningAuditEvent,
} from '../types/planning';
import { ENGINE_VERSION } from './planningEngine';
import { derivePlanningInputTags } from './planningInputTags';
import { readStoredPlanDecision } from './planningStoredDecisions';
import {
  findMirrorPair,
  isHardSequenceEdge,
  normalizeSequencePair,
} from './sequenceRelationships';
import {
  itemRelationshipEdgeRowToDomain,
  type ItemRelationshipEdgeRow,
} from '../types/prioritization/itemRelationships';

const TABLE_PLAN_CYCLES = 'plan_cycles';
const TABLE_PLAN_VERSIONS = 'plan_versions';
const TABLE_PLAN_OF_RECORD = 'plan_of_record';
const TABLE_PLANNING_AUDIT = 'planning_audit_events';
const TABLE_POR_SNAPSHOTS = 'plan_of_record_snapshots';
const TABLE_PLAN_CONSTRAINT_EVALUATIONS = 'plan_constraint_evaluations';
const TABLE_RANK_SEQUENCE_DIVERGENCES = 'rank_sequence_divergences';
const TABLE_TRADEOFF_DECISIONS = 'tradeoff_decisions';
const TABLE_REPRIORITIZATION_TRIGGERS = 'reprioritization_triggers';
const TABLE_PLAN_SCENARIOS = 'plan_scenarios';
const TABLE_SCENARIO_SCORES = 'scenario_scores';
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/**
 * Resolve item titles from the items table (prioritization) for display in Planning.
 * Initiatives reference item_id; this returns a map id -> title for those ids.
 * Read-only: does not modify items or any prioritization state.
 */
/** Titles for a set of decisions — the change log's "because" line resolves through this. */
export async function getDecisionTitlesByIds(
  decisionIds: string[]
): Promise<{ data: Record<string, string>; error: string | null }> {
  try {
    if (decisionIds.length === 0) return { data: {}, error: null };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('decisions')
      .select('id, title')
      .in('id', decisionIds);
    if (error) return { data: {}, error: error.message };
    const map: Record<string, string> = {};
    for (const row of data ?? []) {
      map[(row as { id: string }).id] = (row as { title: string }).title;
    }
    return { data: map, error: null };
  } catch (e) {
    return { data: {}, error: e instanceof Error ? e.message : 'Failed to load decision titles' };
  }
}

/**
 * Item titles by id. With a plan named, an item the plan holds reads the title of the
 * version it authorized (migration 1060, roadmap S-37): a title changed since stays out
 * of the plan until it is amended. Other items read live.
 */
export async function getItemTitlesByIds(
  itemIds: string[],
  planCycleId?: string | null,
): Promise<{ data: Record<string, string>; error: string | null }> {
  try {
    if (itemIds.length === 0) return { data: {}, error: null };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('items')
      .select('id, title')
      .in('id', itemIds);
    if (error) return { data: {}, error: error.message };
    const map: Record<string, string> = {};
    for (const row of data ?? []) {
      const r = row as Record<string, unknown>;
      const id = (r.id ?? r.item_id) as string;
      const title = (r.title ?? r.name) as string | null | undefined;
      if (id) map[id] = (typeof title === 'string' && title.trim()) ? title.trim() : `Item ${id.slice(0, 8)}`;
    }
    if (planCycleId) {
      const held = await fetchHeldValues(itemIds, planCycleId);
      if (!held) return { data: map, error: 'Could not read what the plan holds' };
      for (const [id, h] of held) if (h.title?.trim()) map[id] = h.title.trim();
    }
    return { data: map, error: null };
  } catch (e) {
    return { data: {}, error: e instanceof Error ? e.message : 'Failed to load item titles' };
  }
}

/** Result of gate check for draft → active (canon 12 §2). */
export interface ActivateGateCheck {
  allowed: boolean;
  blockers: string[];
}

/**
 * Check whether a draft PlanCycle can transition to active (canon 12 §2).
 * Requires: at least one PlanVersion, exactly one authorized PlanOfRecord.
 */
export async function canActivatePlanCycle(planCycleId: string): Promise<{
  data: ActivateGateCheck | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const blockers: string[] = [];

    // Fetch cycle to determine whether POR is required for this input type.
    // published_priority_list plans require a POR; all other input types (item_pool,
    // collection, provisional_plan, previous_carry_forward) only require a plan version.
    const { data: cycleRow, error: cycleErr } = await supabase
      .from(TABLE_PLAN_CYCLES)
      .select('input_type')
      .eq('id', planCycleId)
      .single();
    if (cycleErr) return { data: null, error: cycleErr.message };
    const requiresPor = (cycleRow as { input_type: string } | null)?.input_type === 'published_priority_list';

    const { count: versionCount, error: vErr } = await supabase
      .from(TABLE_PLAN_VERSIONS)
      .select('id', { count: 'exact', head: true })
      .eq('plan_cycle_id', planCycleId);
    if (vErr) return { data: null, error: vErr.message };
    if ((versionCount ?? 0) < 1) blockers.push('At least one Plan Version is required.');

    if (requiresPor) {
      const { count: porCount, error: pErr } = await supabase
        .from(TABLE_POR_SNAPSHOTS)
        .select('id', { count: 'exact', head: true })
        .eq('plan_cycle_id', planCycleId)
        .is('superseded_by_snapshot_id', null);
      if (pErr) return { data: null, error: pErr.message };
      if ((porCount ?? 0) !== 1) blockers.push('Exactly one active Plan of Record is required.');
    }

    return {
      data: { allowed: blockers.length === 0, blockers },
      error: null,
    };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to check activate gates',
    };
  }
}

/**
 * Persist audit event. Must not throw; audit failure must not block transitions (build contract 2.5).
 */
async function recordPlanningAudit(params: {
  event_type: string;
  plan_cycle_id?: string;
  plan_version_id?: string;
  plan_of_record_id?: string;
  actor_id?: string | null;
  reason?: string | null;
  payload?: Record<string, unknown>;
}): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    await supabase.from(TABLE_PLANNING_AUDIT).insert({
      event_type: params.event_type,
      plan_cycle_id: params.plan_cycle_id ?? null,
      plan_version_id: params.plan_version_id ?? null,
      plan_of_record_id: params.plan_of_record_id ?? null,
      actor_id: params.actor_id ?? null,
      reason: params.reason ?? null,
      payload: params.payload ?? {},
    });
  } catch {
    // Non-blocking: do not throw; caller may surface audit failure as warning
  }
}

/**
 * Transition PlanCycle from draft → active (canon 12 §2).
 *
 * Build contract §2.3a: ball lane transition — calls the `activate_plan_cycle`
 * SECURITY DEFINER RPC (migration 540). Gate evaluation, status update, and audit event
 * are all atomic within the RPC transaction. No direct client UPDATE.
 *
 * The RPC enforces:
 *  - Cycle must be in draft status
 *  - At least one plan_version must exist (all input types)
 *  - published_priority_list plans additionally require one active POR snapshot
 */
export async function activatePlanCycle(
  planCycleId: string,
  options?: { actor_id?: string | null; reason?: string | null }
): Promise<{ data: PlanCycle | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const tenant = getTenantContext();
    const actorId = options?.actor_id ?? tenant?.userId ?? null;

    const { data, error } = await supabase.rpc('activate_plan_cycle', {
      p_plan_cycle_id: planCycleId,
      p_actor_id: actorId,
      p_reason: options?.reason ?? null,
    });

    if (error) {
      // RPC raises exceptions for gate blockers — surface the message directly.
      return { data: null, error: error.message };
    }

    return { data: data as PlanCycle, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to activate plan cycle',
    };
  }
}

/** Closure decision per initiative (Carry Forward vs Stop & Recompose). */
export type PlanClosureDecision = 'carry_forward' | 'stop_recompose';

/** Unfinished initiative for closure modal. */
export interface UnfinishedInitiativeForClosure {
  initiative_id: string;
  item_id: string;
  priority_position: number | null;
  status: string;
}

/**
 * Get unfinished initiatives (status !== 'complete') for the active POR, for closure UI.
 */
export async function getUnfinishedInitiativesForClosure(planCycleId: string): Promise<{
  data: UnfinishedInitiativeForClosure[];
  error: string | null;
}> {
  try {
    const { data: por, error: porErr } = await getActivePlanOfRecord(planCycleId);
    if (porErr || !por) return { data: [], error: porErr ?? null };
    const supabase = getSupabaseClient();
    const { data: rows, error } = await supabase
      .from('initiatives')
      .select('id, item_id, priority_position, status')
      .eq('plan_version_id', por.plan_version_id)
      .neq('status', 'complete');
    if (error) return { data: [], error: error.message };
    const list = (rows ?? []).map((r) => ({
      initiative_id: r.id,
      item_id: r.item_id,
      priority_position: r.priority_position,
      status: r.status,
    }));
    return { data: list as UnfinishedInitiativeForClosure[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load unfinished initiatives' };
  }
}

/**
 * Transition PlanCycle from active to closed (canon 12 §2).
 *
 * One call to `close_plan_cycle` (migration 787). It used to be five round
 * trips from the browser — check the role, read the unfinished initiatives,
 * insert each closure decision, update the status, write the audit events —
 * with no transaction around them, and with the rule "every unfinished
 * initiative needs a Carry Forward or Stop & Recompose decision" living only
 * here in JavaScript. Anything else writing plan_cycles closed a cycle with no
 * decisions at all, and a failure part-way left decisions attached to a cycle
 * still open.
 *
 * The authority check is no longer duplicated here either: the RPC enforces the
 * same bar as authorizing the Plan of Record. There is no skipRoleCheck,
 * because there is no client-side check left to skip.
 */
export async function closePlanCycle(
  planCycleId: string,
  options?: {
    actor_id?: string | null;
    reason?: string | null;
    decisions?: { initiative_id: string; item_id: string; decision: PlanClosureDecision }[];
  }
): Promise<{ data: PlanCycle | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const tenant = getTenantContext();

    const { data, error } = await supabase.rpc('close_plan_cycle', {
      p_plan_cycle_id: planCycleId,
      p_actor_id: options?.actor_id ?? tenant?.userId ?? null,
      p_reason: options?.reason ?? null,
      p_decisions: options?.decisions ?? [],
    });

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: 'Close returned no cycle.' };

    return { data: data as PlanCycle, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to close plan cycle',
    };
  }
}

/**
 * List plan cycles (optionally scoped by workspace).
 */
export async function listPlanCycles(options?: {
  workspaceId?: string | null;
  status?: PlanCycle['status'];
}): Promise<{ data: PlanCycle[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    let q = supabase
      .from(TABLE_PLAN_CYCLES)
      .select('*')
      .order('created_at', { ascending: false });

    const workspaceId = options?.workspaceId ?? getTenantContext()?.workspaceId ?? getCurrentWorkspaceId() ?? null;
    if (!workspaceId) {
      return { data: [], error: null };
    }
    q = q.eq('workspace_id', workspaceId);
    if (options?.status) {
      q = q.eq('status', options.status);
    }

    const { data, error } = await q;
    if (error) return { data: [], error: error.message };
    const rows = ((data ?? []) as PlanCycle[]).map((c) => ({
      ...c,
      is_primary: Boolean(c.is_primary),
    }));
    return { data: sortPlanCyclesForDisplay(rows), error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list plan cycles' };
  }
}

/** Designate one plan cycle as the workspace primary Plan of Record home. */
export async function setPrimaryPlanCycle(planCycleId: string): Promise<{ data: boolean; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('set_primary_plan_cycle', {
      p_plan_cycle_id: planCycleId,
    });
    if (error) return { data: false, error: error.message };
    const res = data as { success: boolean; error?: string };
    if (!res?.success) return { data: false, error: res?.error ?? 'Failed to set primary plan.' };
    return { data: true, error: null };
  } catch (e) {
    return { data: false, error: e instanceof Error ? e.message : 'Failed to set primary plan.' };
  }
}

/**
 * Get a single plan cycle by id.
 */
export async function getPlanCycleById(id: string): Promise<{
  data: PlanCycle | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_CYCLES)
      .select('*')
      .eq('id', id)
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanCycle, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get plan cycle' };
  }
}

// ─── Scenarios ────────────────────────────────────────────────────────────────

export interface PlanScenarioRow {
  id: string;
  plan_cycle_id: string;
  name: string;
  created_at: string;
  /** Joined from scenario_scores (most recent per score_type) */
  scores?: Array<{ score_type: string; value: number; payload: Record<string, unknown> }>;
}

export async function listScenariosForPlanCycle(planCycleId: string): Promise<{
  data: PlanScenarioRow[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_SCENARIOS)
      .select('*, scenario_scores(*)')
      .eq('plan_cycle_id', planCycleId)
      .order('created_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((s: Record<string, unknown>) => ({
        id: s.id as string,
        plan_cycle_id: s.plan_cycle_id as string,
        name: s.name as string,
        created_at: s.created_at as string,
        scores: (s.scenario_scores as Array<{ score_type: string; value: number; payload: Record<string, unknown> }>) ?? [],
      })),
      error: null,
    };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list scenarios' };
  }
}

export async function createScenario(
  planCycleId: string,
  name: string,
): Promise<{ data: PlanScenarioRow | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_SCENARIOS)
      .insert({ plan_cycle_id: planCycleId, name })
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanScenarioRow, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create scenario' };
  }
}

/**
 * Persist a computed scenario score vector to scenario_scores (Doc 19 §7).
 * Writes one row per score dimension (feasibility, divergence_penalty, risk_penalty).
 */
export async function persistScenarioScore(
  scenarioId: string,
  scoreVector: { feasibility_score: number; divergence_penalty: number; risk_penalty: number; engine_version: string },
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const rows = [
      { plan_scenario_id: scenarioId, score_type: 'feasibility', value: scoreVector.feasibility_score, payload: { engine_version: scoreVector.engine_version } },
      { plan_scenario_id: scenarioId, score_type: 'divergence_penalty', value: scoreVector.divergence_penalty, payload: {} },
      { plan_scenario_id: scenarioId, score_type: 'risk_penalty', value: scoreVector.risk_penalty, payload: {} },
    ];
    const { error } = await supabase.from(TABLE_SCENARIO_SCORES).insert(rows);
    return { error: error?.message ?? null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to persist scenario score' };
  }
}

// ─── por_snapshot_view ────────────────────────────────────────────────────────

/**
 * One-row summary for a plan cycle assembled from por_snapshot_view (migration 541).
 * Replaces 5+ parallel queries in Plan Detail with a single round-trip.
 */
export interface PorSnapshotRow {
  plan_cycle_id: string;
  name: string;
  status: string;
  input_type: string;
  workspace_id: string | null;
  created_by: string | null;
  start_condition: string | null;
  cycle_created_at: string | null;
  // Latest plan version
  plan_version_id: string | null;
  version_number: number | null;
  engine_version: string | null;
  change_reason: string | null;
  version_created_at: string | null;
  // Active POR snapshot
  snapshot_id: string | null;
  authorized_at: string | null;
  authorized_by: string | null;
  // Latest constraint evaluation
  evaluation_id: string | null;
  evaluation_status: string | null;
  evaluation_summary: Record<string, unknown> | null;
  evaluation_details: Record<string, unknown> | null;
  evaluation_computed_at: string | null;
  // Aggregate counts
  initiative_count: number;
  open_divergence_count: number;
  total_divergence_count: number;
  tradeoff_count: number;
}

/**
 * Acknowledge a constraint evaluation — marks the planner as having reviewed the results.
 * Emits EVALUATION_ACKNOWLEDGED audit event and records a tradeoff_decision for traceability.
 * Distinct from Override Policy (which is for severe violations requiring rationale).
 */
export async function acknowledgeConstraintEvaluation(
  evaluationId: string,
  planVersionId: string,
  options?: { acknowledged_by?: string | null }
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const actor = options?.acknowledged_by ?? getTenantContext()?.userId ?? null;
    // §2.3a: evaluation status is an authority-adjacent field; guard it.
    // 'acknowledged' is a valid superset of 'computed' — the gate (evaluationComputed)
    // accepts both. We update only from 'computed' to prevent double-acknowledgment.
    const { error } = await supabase
      .from(TABLE_PLAN_CONSTRAINT_EVALUATIONS)
      .update({ status: 'acknowledged' })
      .eq('id', evaluationId)
      .eq('status', 'computed'); // idempotent guard — only transitions from computed
    if (error) return { error: error.message };

    // Audit event
    const { data: pv } = await supabase.from(TABLE_PLAN_VERSIONS).select('plan_cycle_id').eq('id', planVersionId).single();
    await recordPlanningAudit({
      event_type: 'EVALUATION_ACKNOWLEDGED',
      plan_cycle_id: pv?.plan_cycle_id ?? undefined,
      plan_version_id: planVersionId,
      actor_id: actor,
      reason: 'Evaluation reviewed and acknowledged',
      payload: { evaluation_id: evaluationId },
    });

    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to acknowledge evaluation' };
  }
}

/**
 * Fetch a single plan cycle's full context from por_snapshot_view (migration 541).
 * Returns one row with cycle metadata, latest version info, active POR snapshot,
 * latest constraint evaluation, and aggregate counts — all in one query.
 */
export async function getPlanSnapshot(cycleId: string): Promise<{
  data: PorSnapshotRow | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('por_snapshot_view')
      .select('*')
      .eq('plan_cycle_id', cycleId)
      .maybeSingle();
    if (error) return { data: null, error: error.message };
    return { data: data as PorSnapshotRow | null, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get plan snapshot' };
  }
}

/**
 * Get the active Plan of Record for a plan cycle (canon 10, 11).
 * Authoritative source: plan_of_record_snapshots WHERE superseded_by_snapshot_id IS NULL.
 */
/** Return type: active POR from snapshot table, with optional plan_version and status for PlanOfRecord compatibility. */
export type ActivePlanOfRecordResult = ActivePlanOfRecord & { plan_version?: PlanVersion; status: 'active' };

export async function getActivePlanOfRecord(planCycleId: string): Promise<{
  data: ActivePlanOfRecordResult | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: snapshot, error: snapError } = await supabase
      .from(TABLE_POR_SNAPSHOTS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .is('superseded_by_snapshot_id', null)
      .maybeSingle();
    if (snapError) return { data: null, error: snapError.message };
    if (!snapshot) return { data: null, error: null };

    const { data: pv, error: pvError } = await supabase
      .from(TABLE_PLAN_VERSIONS)
      .select('*')
      .eq('id', snapshot.plan_version_id)
      .single();
    const withStatus = { ...snapshot, status: 'active' as const };
    if (pvError) return { data: { ...withStatus, plan_version: undefined } as ActivePlanOfRecordResult, error: null };
    return {
      data: { ...withStatus, plan_version: pv } as ActivePlanOfRecordResult,
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get plan of record' };
  }
}

/**
 * List plan versions for a cycle (version_number ascending). For replan / authorize UI.
 */
export async function listPlanVersions(planCycleId: string): Promise<{
  data: PlanVersion[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_VERSIONS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('version_number', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as PlanVersion[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list plan versions' };
  }
}

/** One priority in a plan version, on whichever side of the line it landed. */
export interface PlanVersionLineEntry {
  priorityId: string;
  name: string;
  /** Where the engine ranked it across the whole plan (1-based). */
  position: number | null;
  /** The engine's call — 'Included', 'Out - capacity', 'Blocked', … */
  decision: string;
  /** Why, when the engine said why. */
  reason: string | null;
  workUnits: number;
}

/**
 * What a plan version actually contains, split at the line.
 *
 * `above` is the commitment — what authorizing this version commits the
 * organization to. `below` is what was considered in the same pass and did not
 * make the cut, kept tied to the version so the record says what was traded
 * away, not only what was kept (owner ruling, 2026-09-23).
 *
 * Reads plan_records, which are immutable: this is the version as committed,
 * not a recomputation.
 */
export async function getPlanVersionLineContents(planVersionId: string): Promise<{
  above: PlanVersionLineEntry[];
  below: PlanVersionLineEntry[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('plan_records')
      .select('planning_priority_id, priority_name, priority_rank, work_unit_sequence, line_position, decision, decision_reason')
      .eq('plan_version_id', planVersionId);
    if (error) return { above: [], below: [], error: error.message };

    type Row = {
      planning_priority_id: string | null;
      priority_name: string | null;
      priority_rank: number | null;
      work_unit_sequence: number | null;
      line_position: string;
      decision: string;
      decision_reason: string | null;
    };

    // One entry per priority. A priority with any above-the-line record is in
    // the plan; its work units below the line are part of what was trimmed, so
    // the priority itself is not listed twice.
    const byPriority = new Map<string, PlanVersionLineEntry & { above: boolean }>();
    for (const row of (data ?? []) as Row[]) {
      const id = row.planning_priority_id;
      if (!id) continue;
      const isAbove = row.line_position === 'above';
      const position = row.priority_rank ?? (row.work_unit_sequence == null ? null : row.work_unit_sequence + 1);
      const existing = byPriority.get(id);
      if (!existing) {
        byPriority.set(id, {
          priorityId: id,
          name: row.priority_name ?? 'Untitled priority',
          position,
          decision: readStoredPlanDecision(row.decision),
          reason: row.decision_reason,
          workUnits: 1,
          above: isAbove,
        });
        continue;
      }
      existing.workUnits += 1;
      if (isAbove && !existing.above) {
        // Partly included: the plan carries it, at its included rank.
        existing.above = true;
        existing.position = position;
        existing.decision = readStoredPlanDecision(row.decision);
        existing.reason = row.decision_reason;
      } else if (isAbove === existing.above && position != null && (existing.position == null || position < existing.position)) {
        existing.position = position;
      }
    }

    const byPosition = (a: PlanVersionLineEntry, b: PlanVersionLineEntry) =>
      (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER);
    const all = [...byPriority.values()];
    return {
      above: all.filter((e) => e.above).map(({ above: _above, ...rest }) => rest).sort(byPosition),
      below: all.filter((e) => !e.above).map(({ above: _above, ...rest }) => rest).sort(byPosition),
      error: null,
    };
  } catch (e) {
    return { above: [], below: [], error: e instanceof Error ? e.message : 'Failed to read the plan version contents' };
  }
}

/**
 * Latest PlanVersion for a cycle (Doc 15, 16). DB is system of record.
 * SELECT * FROM plan_versions WHERE plan_cycle_id = $1 ORDER BY version_number DESC LIMIT 1
 */
export async function getLatestPlanVersion(planCycleId: string): Promise<{
  data: PlanVersion | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_VERSIONS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanVersion | null, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get latest plan version' };
  }
}

/**
 * Current POR snapshot for a cycle (Doc 10, 11). Authoritative: plan_of_record_snapshots WHERE superseded_by_snapshot_id IS NULL.
 * SELECT * FROM plan_of_record_snapshots WHERE plan_cycle_id = $1 AND superseded_by_snapshot_id IS NULL
 */
export async function getCurrentPorSnapshot(planCycleId: string): Promise<{
  data: PlanOfRecordSnapshot | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_POR_SNAPSHOTS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .is('superseded_by_snapshot_id', null)
      .maybeSingle();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanOfRecordSnapshot | null, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get current POR snapshot' };
  }
}

/**
 * Latest evaluation for a plan version (Doc 15, 16).
 * SELECT * FROM plan_constraint_evaluations WHERE plan_version_id = $1 ORDER BY created_at DESC LIMIT 1
 */
export async function getLatestEvaluation(planVersionId: string): Promise<{
  data: PlanConstraintEvaluation | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_CONSTRAINT_EVALUATIONS)
      .select('*')
      .eq('plan_version_id', planVersionId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanConstraintEvaluation | null, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get latest evaluation' };
  }
}

/**
 * Run the planning engine (Doc 19): optionally materialize buckets, then run constraint evaluation and persist.
 * Use this to produce/refresh constraint check results for a plan version.
 */
export async function runPlanVersionEvaluation(planVersionId: string, options?: {
  materializeBuckets?: boolean;
  evaluated_by?: string | null;
}): Promise<{ data: { evaluationId: string } | null; error: string | null }> {
  const { materializeBucketsForPlanVersion, runAndPersistConstraintEvaluation } = await import('./planningEngine');
  if (options?.materializeBuckets !== false) {
    // Self-heal: engine-committed versions created before governance-record
    // synthesis existed have plan_records but no initiatives/work_units, so
    // bucket allocations could never materialize. Backfill from the version's
    // own records first (idempotent no-op when initiatives already exist).
    const { backfillGovernanceRecordsForPlanVersion } = await import('./planningEngineService');
    const backfillRes = await backfillGovernanceRecordsForPlanVersion(planVersionId);
    if (backfillRes.error) return { data: null, error: backfillRes.error };
    const bucketRes = await materializeBucketsForPlanVersion(planVersionId);
    if (bucketRes.error) return { data: null, error: bucketRes.error };
  }
  const evaluation = await runAndPersistConstraintEvaluation(planVersionId, { evaluated_by: options?.evaluated_by });
  if (evaluation.error || !evaluation.data) return evaluation;
  const divergences = await runAndPersistDivergences(planVersionId, {
    caused_by_evaluation_id: evaluation.data.evaluationId,
  });
  if (divergences.error) return { data: null, error: divergences.error };
  return evaluation;
}

/**
 * Record what this version shows that differs from its rank (Doc 19 §6). The
 * database decides what counts; running it again records nothing new.
 */
export async function runAndPersistDivergences(planVersionId: string, options?: {
  caused_by_evaluation_id?: string | null;
}): Promise<{ data: { recorded: number }; error: string | null }> {
  const { detectPlanDivergencesRpc } = await import('./planningRpcService');
  const { data, error } = await detectPlanDivergencesRpc({
    plan_version_id: planVersionId,
    caused_by_evaluation_id: options?.caused_by_evaluation_id ?? null,
  });
  return { data: { recorded: data }, error };
}

/**
 * Divergences for a plan version (Doc 15 §4). From rank_sequence_divergences.
 */
export async function listDivergencesForPlanVersion(planVersionId: string): Promise<{
  data: RankSequenceDivergence[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_RANK_SEQUENCE_DIVERGENCES)
      .select('*')
      .eq('plan_version_id', planVersionId)
      .order('created_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as RankSequenceDivergence[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list divergences' };
  }
}

/**
 * Tradeoffs for a plan version (Doc 15, 17). From tradeoff_decisions.
 */
export async function listTradeoffsForPlanVersion(planVersionId: string): Promise<{
  data: TradeoffDecision[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_TRADEOFF_DECISIONS)
      .select('*')
      .eq('plan_version_id', planVersionId)
      .order('created_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as TradeoffDecision[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list tradeoffs' };
  }
}

/** Reprioritization trigger row (reprioritization_triggers). */
export interface ReprioritizationTriggerRow {
  id: string;
  plan_cycle_id: string;
  status: string;
  reason: Record<string, unknown>;
  triggered_by: string | null;
  triggered_at: string;
  plan_constraint_evaluation_id: string | null;
  created_at: string;
}

/**
 * List reprioritization triggers for a plan cycle (Doc 15 §5.1).
 */
export async function listReprioritizationTriggersForPlanCycle(planCycleId: string): Promise<{
  data: ReprioritizationTriggerRow[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_REPRIORITIZATION_TRIGGERS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('triggered_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as ReprioritizationTriggerRow[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list reprioritization triggers' };
  }
}

/**
 * Create a reprioritization recommendation (status=recommended). Does not update plan_cycles.
 * Link to evidence via plan_constraint_evaluation_id when constraint pressure prompts recommendation.
 */
export async function recommendReprioritization(
  planCycleId: string,
  reason: Record<string, unknown>,
  options?: { triggered_by?: string | null; plan_constraint_evaluation_id?: string | null }
): Promise<{ data: string | null; error: string | null }> {
  try {
    const { createReprioritizationRecommendationRpc } = await import('./planningRpcService');
    const res = await createReprioritizationRecommendationRpc({
      plan_cycle_id: planCycleId,
      reason_json: reason,
      triggered_by: options?.triggered_by ?? getTenantContext()?.userId ?? null,
      plan_constraint_evaluation_id: options?.plan_constraint_evaluation_id ?? null,
    });
    return { data: res.data, error: res.error };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Recommend reprioritization failed' };
  }
}

/**
 * Open reprioritization for a plan cycle (explicit governance action). Updates plan_cycles; creates trigger with status 'open'.
 */
export async function openReprioritizationTrigger(
  planCycleId: string,
  reason: Record<string, unknown>,
  options?: { triggered_by?: string | null; plan_constraint_evaluation_id?: string | null }
): Promise<{ data: string | null; error: string | null }> {
  try {
    const { openReprioritizationTriggerRpc } = await import('./planningRpcService');
    const res = await openReprioritizationTriggerRpc({
      plan_cycle_id: planCycleId,
      reason_json: reason,
      triggered_by: options?.triggered_by ?? getTenantContext()?.userId ?? null,
      plan_constraint_evaluation_id: options?.plan_constraint_evaluation_id ?? null,
    });
    return { data: res.data, error: res.error };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Open reprioritization trigger failed' };
  }
}

/**
 * Acknowledge a reprioritization trigger (set status to acknowledged).
 */
export async function acknowledgeReprioritizationTrigger(triggerId: string): Promise<{ data: null; error: string | null }> {
  try {
    const { updateReprioritizationTriggerStatusRpc } = await import('./planningRpcService');
    return updateReprioritizationTriggerStatusRpc({ trigger_id: triggerId, status: 'acknowledged' });
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Acknowledge reprioritization trigger failed' };
  }
}

/**
 * Close a reprioritization trigger (set status to closed).
 */
export async function closeReprioritizationTrigger(triggerId: string): Promise<{ data: null; error: string | null }> {
  try {
    const { updateReprioritizationTriggerStatusRpc } = await import('./planningRpcService');
    return updateReprioritizationTriggerStatusRpc({ trigger_id: triggerId, status: 'closed' });
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Close reprioritization trigger failed' };
  }
}

/** Allowed resolution_status values for divergence (Doc 18 §4.1). */
const DIVERGENCE_RESOLUTION_STATUSES = ['accepted', 'mitigated', 'disputed'] as const;

/**
 * Update a divergence's resolution status (accept / mitigate / dispute). Doc 18 §4.1.
 * Calls update_divergence_resolution RPC; optionally emits audit.
 */
export async function updateDivergenceResolution(
  divergenceId: string,
  resolutionStatus: string,
  reason: string,
): Promise<{ data: null; error: string | null }> {
  const status = resolutionStatus?.trim().toLowerCase();
  if (!DIVERGENCE_RESOLUTION_STATUSES.includes(status as (typeof DIVERGENCE_RESOLUTION_STATUSES)[number])) {
    return { data: null, error: `resolution_status must be one of: ${DIVERGENCE_RESOLUTION_STATUSES.join(', ')}` };
  }
  const { resolveDivergenceRpc } = await import('./planningRpcService');
  return resolveDivergenceRpc({ divergence_id: divergenceId, resolution_status: status, reason });
}

/**
 * Create a governance override artifact for severe constraint violations (Doc 14 §5).
 * Persists a tradeoff_decision with who, what, why, timestamp, and link to evaluation.
 * Required for POR authorization when severe violations are present.
 */
export async function createGovernanceOverrideForViolations(
  planVersionId: string,
  evaluationId: string,
  reason: string,
  options?: { accepted_by?: string | null }
): Promise<{ data: string | null; error: string | null }> {
  try {
    const { recordTradeoffRpc } = await import('./planningRpcService');
    const acceptedBy = options?.accepted_by ?? getTenantContext()?.userId ?? null;
    const { data: tradeoffId, error: rpcErr } = await recordTradeoffRpc({
      plan_version_id: planVersionId,
      tradeoff_kind: 'violation_override',
      payload_json: {
        constraint_evaluation_id: evaluationId,
        reason: reason.trim(),
        why: reason.trim(),
      },
      accepted_by: acceptedBy,
    });
    if (rpcErr || !tradeoffId) return { data: null, error: rpcErr ?? 'Failed to record override' };
    const supabase = getSupabaseClient();
    const { data: pv } = await supabase.from(TABLE_PLAN_VERSIONS).select('plan_cycle_id').eq('id', planVersionId).single();
    await recordPlanningAudit({
      event_type: 'POLICY_OVERRIDDEN',
      plan_cycle_id: pv?.plan_cycle_id ?? undefined,
      plan_version_id: planVersionId,
      actor_id: acceptedBy ?? undefined,
      reason: reason.slice(0, 500),
      payload: { evidence_refs: { constraint_evaluation_id: evaluationId, tradeoff_decision_id: tradeoffId } },
    });
    return { data: tradeoffId, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Create governance override failed' };
  }
}

/**
 * Create a governance override artifact for unresolved divergence (Doc 14 §5).
 * Persists a tradeoff_decision with who, what, why, timestamp, and link to divergence_ids.
 * Required for POR authorization when divergence is not resolved (accepted/mitigated).
 */
export async function createGovernanceOverrideForDivergence(
  planVersionId: string,
  divergenceIds: string[],
  reason: string,
  options?: { accepted_by?: string | null }
): Promise<{ data: string | null; error: string | null }> {
  try {
    if (divergenceIds.length === 0) return { data: null, error: 'divergence_ids required' };
    const { recordTradeoffRpc } = await import('./planningRpcService');
    const acceptedBy = options?.accepted_by ?? getTenantContext()?.userId ?? null;
    const { data: tradeoffId, error: rpcErr } = await recordTradeoffRpc({
      plan_version_id: planVersionId,
      tradeoff_kind: 'divergence_override',
      payload_json: {
        divergence_ids: divergenceIds,
        reason: reason.trim(),
        why: reason.trim(),
      },
      accepted_by: acceptedBy,
    });
    if (rpcErr || !tradeoffId) return { data: null, error: rpcErr ?? 'Failed to record override' };
    const supabase = getSupabaseClient();
    const { data: pv } = await supabase.from(TABLE_PLAN_VERSIONS).select('plan_cycle_id').eq('id', planVersionId).single();
    await recordPlanningAudit({
      event_type: 'DIVERGENCE_ACCEPTED',
      plan_cycle_id: pv?.plan_cycle_id ?? undefined,
      plan_version_id: planVersionId,
      actor_id: acceptedBy ?? undefined,
      reason: reason.slice(0, 500),
      payload: { evidence_refs: { divergence_ids: divergenceIds, tradeoff_decision_id: tradeoffId } },
    });
    return { data: tradeoffId, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Create divergence override failed' };
  }
}


/**
 * Validate that the dependency graph for a plan version remains acyclic with an optional new link (Doc 13, 15).
 */
export async function validateDependencyAcyclic(
  planVersionId: string,
  candidateLink?: { from_work_unit_id: string; to_work_unit_id: string }
): Promise<{ valid: boolean; reason?: string }> {
  try {
    const supabase = getSupabaseClient();
    const { data: initRows } = await supabase.from('initiatives').select('id').eq('plan_version_id', planVersionId);
    const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
    if (initiativeIds.length === 0) return { valid: true };

    const { data: wuRows } = await supabase.from('work_units').select('id').in('initiative_id', initiativeIds);
    const wuIds = new Set((wuRows ?? []).map((r: { id: string }) => r.id));
    if (wuIds.size === 0) return { valid: true };

    const { data: linkRows } = await supabase.from('dependency_links').select('from_work_unit_id, to_work_unit_id');
    let links = (linkRows ?? []).map((r: { from_work_unit_id: string; to_work_unit_id: string }) => ({
      from_work_unit_id: r.from_work_unit_id,
      to_work_unit_id: r.to_work_unit_id,
    }));
    links = links.filter((l) => wuIds.has(l.from_work_unit_id) && wuIds.has(l.to_work_unit_id));
    if (candidateLink && wuIds.has(candidateLink.from_work_unit_id) && wuIds.has(candidateLink.to_work_unit_id)) {
      links = [...links, candidateLink];
    }

    const inDegree = new Map<string, number>();
    const outEdges = new Map<string, string[]>();
    wuIds.forEach((id) => {
      inDegree.set(id, 0);
      outEdges.set(id, []);
    });
    for (const l of links) {
      inDegree.set(l.to_work_unit_id, (inDegree.get(l.to_work_unit_id) ?? 0) + 1);
      outEdges.get(l.from_work_unit_id)!.push(l.to_work_unit_id);
    }
    const queue = Array.from(wuIds).filter((id) => inDegree.get(id) === 0);
    const order: string[] = [];
    while (queue.length > 0) {
      const id = queue.shift()!;
      order.push(id);
      for (const toId of outEdges.get(id) ?? []) {
        const d = (inDegree.get(toId) ?? 0) - 1;
        inDegree.set(toId, d);
        if (d === 0) queue.push(toId);
      }
    }
    if (order.length !== wuIds.size) return { valid: false, reason: 'Circular dependency' };
    return { valid: true };
  } catch (e) {
    return { valid: false, reason: e instanceof Error ? e.message : 'Validation failed' };
  }
}

/**
 * Resolve the execution recipe for a work unit at creation time.
 * Canon Doc 21 §4.2 / Integration Guide Step 7.
 * Returns the recipe template id, or null if work_type is unset (→ recipe_pending).
 */
async function resolveRecipeForItem(itemId: string): Promise<string | null> {
  const supabase = getSupabaseClient();
  const { data: item } = await supabase
    .from('items')
    .select('work_type, workspace_id')
    .eq('id', itemId)
    .single();
  if (!item?.work_type) return null;
  return resolveRecipeIdForWorkType(item.work_type, item.workspace_id as string | null);
}

/**
 * Create a WorkUnit under an initiative (Doc 13 §2 Step 3).
 * Recipe resolution (Canon Doc 21 §4.2, Step 2a) runs immediately after insert:
 * - resolved_recipe_id is set when items.work_type is populated.
 * - If work_type is null, resolved_recipe_id stays null → recipe_pending state.
 * - A planning_audit_events row is emitted when resolution succeeds.
 */
export async function createWorkUnit(
  initiativeId: string,
  payload: {
    title: string;
    description?: string | null;
    readiness_state?: WorkUnitReadinessState;
    effort_score?: number | null;
    duration_score?: number | null;
    estimated_effort_hours?: number | null;
    estimated_duration_days?: number | null;
    [key: string]: unknown;
  }
): Promise<{ data: WorkUnit | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();

    // Resolve recipe before insert: look up item_id via initiative
    let resolvedRecipeId: string | null = null;
    let itemId: string | null = null;
    const { data: initiative } = await supabase
      .from('initiatives')
      .select('item_id, plan_version_id')
      .eq('id', initiativeId)
      .single();
    if (initiative?.item_id) {
      itemId = initiative.item_id;
      resolvedRecipeId = await resolveRecipeForItem(initiative.item_id);
    }

    const { data, error } = await supabase
      .from('work_units')
      .insert({
        initiative_id: initiativeId,
        title: payload.title,
        description: payload.description ?? null,
        readiness_state: payload.readiness_state ?? 'not_ready',
        effort_score: payload.effort_score ?? null,
        duration_score: payload.duration_score ?? null,
        estimated_effort_hours: payload.estimated_effort_hours ?? null,
        estimated_duration_days: payload.estimated_duration_days ?? null,
        complexity_score: payload.complexity_score ?? null,
        uncertainty_score: payload.uncertainty_score ?? null,
        risk_score: payload.risk_score ?? null,
        buffer_score: payload.buffer_score ?? null,
        confidence_score: payload.confidence_score ?? null,
        coordination_load_score: payload.coordination_load_score ?? null,
        skill_specificity_score: payload.skill_specificity_score ?? null,
        resolved_recipe_id: resolvedRecipeId,
      })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };

    const workUnit = data as WorkUnit;

    // Emit audit event when recipe resolved (Canon Doc 21 §5.2)
    if (resolvedRecipeId && initiative?.plan_version_id) {
      await supabase.from('planning_audit_events').insert({
        event_type: 'recipe_resolved',
        plan_version_id: initiative.plan_version_id,
        work_unit_id: workUnit.id,
        payload: {
          work_type: (await supabase.from('items').select('work_type').eq('id', itemId!).single()).data?.work_type ?? null,
          resolved_recipe_id: resolvedRecipeId,
          resolved_by: 'system',
          resolved_at: new Date().toISOString(),
        },
      });
    }

    return { data: workUnit, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create work unit' };
  }
}

/**
 * Create a DependencyLink; rejects if it would introduce a cycle (Doc 13, 15).
 */
export async function createDependencyLink(
  planVersionId: string,
  fromWorkUnitId: string,
  toWorkUnitId: string,
  dependencyType: DependencyTypeEnum
): Promise<{ data: DependencyLink | null; error: string | null }> {
  try {
    const check = await validateDependencyAcyclic(planVersionId, {
      from_work_unit_id: fromWorkUnitId,
      to_work_unit_id: toWorkUnitId,
    });
    if (!check.valid) return { data: null, error: check.reason ?? 'Circular dependency' };

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('dependency_links')
      .insert({
        from_work_unit_id: fromWorkUnitId,
        to_work_unit_id: toWorkUnitId,
        dependency_type: dependencyType,
      })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as DependencyLink, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create dependency link' };
  }
}

export interface SyncItemDependenciesResult {
  created: number;
  skipped: number;
  missingItems: string[];
  mirrorPairs: number;
  rollupWorkUnitsCreated: number;
}

/**
 * Import hard item sequence relationships into plan work-unit dependency_links (FS default).
 * Uses the first work unit per initiative; creates rollup work units when none exist.
 */
export async function syncItemDependenciesToPlan(
  planVersionId: string,
  options?: { createMissingRollupWorkUnits?: boolean },
): Promise<{ data: SyncItemDependenciesResult | null; error: string | null }> {
  const createRollup = options?.createMissingRollupWorkUnits ?? true;
  const result: SyncItemDependenciesResult = {
    created: 0,
    skipped: 0,
    missingItems: [],
    mirrorPairs: 0,
    rollupWorkUnitsCreated: 0,
  };

  try {
    const supabase = getSupabaseClient();

    const { data: initRows, error: initErr } = await supabase
      .from('initiatives')
      .select('id, item_id, title')
      .eq('plan_version_id', planVersionId);
    if (initErr) return { data: null, error: initErr.message };

    const initiatives = (initRows ?? []) as Initiative[];
    const itemToInitiative = new Map<string, string>();
    const initiativeTitles = new Map<string, string>();
    const planItemIds = new Set<string>();
    for (const i of initiatives) {
      if (!i.item_id) continue;
      itemToInitiative.set(i.item_id, i.id);
      planItemIds.add(i.item_id);
      if (i.title) initiativeTitles.set(i.id, i.title);
    }

    if (planItemIds.size === 0) {
      return { data: result, error: null };
    }

    const initiativeIds = initiatives.map((i) => i.id);
    const { data: wuRows } = await supabase
      .from('work_units')
      .select('*')
      .in('initiative_id', initiativeIds);
    const workUnits = (wuRows ?? []) as WorkUnit[];

    const initiativeToPrimaryWu = new Map<string, string>();
    for (const wu of workUnits) {
      if (!initiativeToPrimaryWu.has(wu.initiative_id)) {
        initiativeToPrimaryWu.set(wu.initiative_id, wu.id);
      }
    }

    if (createRollup) {
      for (const init of initiatives) {
        if (!init.item_id || initiativeToPrimaryWu.has(init.id)) continue;
        const title =
          initiativeTitles.get(init.id) ??
          (await getItemTitlesByIds([init.item_id])).data[init.item_id] ??
          `Item ${init.item_id.slice(0, 8)}`;
        const { data: created, error: wuErr } = await createWorkUnit(init.id, {
          title,
          readiness_state: 'not_ready',
        });
        if (wuErr || !created) continue;
        initiativeToPrimaryWu.set(init.id, created.id);
        result.rollupWorkUnitsCreated += 1;
      }
    }

    const itemToWu = new Map<string, string>();
    for (const init of initiatives) {
      if (!init.item_id) continue;
      const wuId = initiativeToPrimaryWu.get(init.id);
      if (wuId) itemToWu.set(init.item_id, wuId);
    }

    const { data: edgeRows, error: edgeErr } = await supabase
      .from('item_relationship_edges')
      .select('*')
      .in('relationship_type', ['dependency', 'prerequisite'])
      .neq('status', 'dissolved');
    if (edgeErr) return { data: null, error: edgeErr.message };

    const edges = ((edgeRows ?? []) as ItemRelationshipEdgeRow[]).map(itemRelationshipEdgeRowToDomain);
    const hardEdges = edges.filter(isHardSequenceEdge);

    const wuIds = [...new Set(itemToWu.values())];
    const { data: existingLinkRows } =
      wuIds.length > 0
        ? await supabase.from('dependency_links').select('*').in('from_work_unit_id', wuIds)
        : { data: [] };
    const existingLinks = (existingLinkRows ?? []) as DependencyLink[];
    const linkKey = (from: string, to: string) => `${from}:${to}`;
    const existingKeys = new Set(
      existingLinks.map((l) => linkKey(l.from_work_unit_id, l.to_work_unit_id)),
    );

    const processedPairs = new Set<string>();

    for (const edge of hardEdges) {
      const normalized = normalizeSequencePair(edge);
      if (!normalized) continue;

      const { predecessorItemId, dependentItemId } = normalized;
      if (!planItemIds.has(predecessorItemId) || !planItemIds.has(dependentItemId)) {
        if (!planItemIds.has(predecessorItemId)) result.missingItems.push(predecessorItemId);
        if (!planItemIds.has(dependentItemId)) result.missingItems.push(dependentItemId);
        continue;
      }

      const pairKey = [predecessorItemId, dependentItemId].join('→');
      if (processedPairs.has(pairKey)) continue;
      processedPairs.add(pairKey);

      if (findMirrorPair(edges, predecessorItemId, dependentItemId)) {
        result.mirrorPairs += 1;
      }

      const fromWu = itemToWu.get(predecessorItemId);
      const toWu = itemToWu.get(dependentItemId);
      if (!fromWu || !toWu) {
        result.skipped += 1;
        continue;
      }

      if (existingKeys.has(linkKey(fromWu, toWu))) {
        // Skip, never update: a link already here may carry a manually chosen
        // Dependency Timing Type (Advanced Planning). Re-syncing must not
        // clobber it back to FS.
        result.skipped += 1;
        continue;
      }

      const { error: linkErr } = await createDependencyLink(planVersionId, fromWu, toWu, 'FS');
      if (linkErr) {
        result.skipped += 1;
        continue;
      }
      existingKeys.add(linkKey(fromWu, toWu));
      result.created += 1;
    }

    result.missingItems = [...new Set(result.missingItems)];
    return { data: result, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to sync item dependencies' };
  }
}

/**
 * Get all WorkUnits for a plan version (via initiatives).
 */
export async function getWorkUnitsForPlanVersion(planVersionId: string): Promise<{
  data: WorkUnit[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: initRows } = await supabase.from('initiatives').select('id').eq('plan_version_id', planVersionId);
    const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
    if (initiativeIds.length === 0) return { data: [], error: null };
    const { data: wuRows, error } = await supabase
      .from('work_units')
      .select('*')
      .in('initiative_id', initiativeIds);
    if (error) return { data: [], error: error.message };
    return { data: (wuRows ?? []) as WorkUnit[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load work units' };
  }
}

/**
 * Create a CapacityAllocation for a plan version (Doc 13 §2 Step 3, 17 §9).
 */
export async function createCapacityAllocation(
  planVersionId: string,
  payload: {
    resource_identifier: string;
    allocation_percentage: number;
    time_window_start?: string | null;
    time_window_end?: string | null;
  }
): Promise<{ data: CapacityAllocation | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('capacity_allocations')
      .insert({
        plan_version_id: planVersionId,
        resource_identifier: payload.resource_identifier,
        allocation_percentage: payload.allocation_percentage,
        time_window_start: payload.time_window_start ?? null,
        time_window_end: payload.time_window_end ?? null,
      })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as CapacityAllocation, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create capacity allocation' };
  }
}

/**
 * Backfill initiative.title from the items table for initiatives that have item_id but no title.
 * Use for plans created before we stored titles at creation. Persists so labels show on reload.
 */
export async function backfillInitiativeTitlesForPlanVersion(planVersionId: string): Promise<{
  updated: number;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: inits, error: fetchErr } = await supabase
      .from('initiatives')
      .select('id, item_id, title')
      .eq('plan_version_id', planVersionId);
    if (fetchErr || !inits?.length) return { updated: 0, error: fetchErr?.message ?? null };
    const needTitle = inits.filter(
      (i: { item_id: string; title?: string | null }) => !(i.title?.trim())
    );
    if (needTitle.length === 0) return { updated: 0, error: null };
    const itemIds = [...new Set(needTitle.map((i: { item_id: string }) => i.item_id).filter(Boolean))];
    const { data: titleMap } = await getItemTitlesByIds(itemIds);
    let updated = 0;
    for (const i of inits as { id: string; item_id: string; title?: string | null }[]) {
      if (i.title?.trim()) continue;
      const title = titleMap[i.item_id];
      if (!title) continue;
      const { error: upErr } = await supabase
        .from('initiatives')
        .update({ title })
        .eq('id', i.id);
      if (!upErr) updated++;
    }
    return { updated, error: null };
  } catch (e) {
    return { updated: 0, error: e instanceof Error ? e.message : 'Backfill failed' };
  }
}

/**
 * Update initiative status. When status is 'dropped', drop_reason is required (canon 12 §5).
 * Emits InitiativeDropped audit event.
 */
export async function updateInitiativeStatus(
  initiativeId: string,
  status: Initiative['status'],
  options?: { drop_reason?: InitiativeDropReason | null; actor_id?: string | null }
): Promise<{ data: Initiative | null; error: string | null }> {
  try {
    if (status === 'dropped') {
      const reason = options?.drop_reason ?? null;
      if (!reason || !['scope_removed', 'deprioritized', 'infeasible', 'other'].includes(reason)) {
        return { data: null, error: 'drop_reason is required when dropping (scope_removed | deprioritized | infeasible | other)' };
      }
    }

    const supabase = getSupabaseClient();
    const tenant = getTenantContext();
    const actorId = options?.actor_id ?? tenant?.userId ?? null;

    const { data: init, error: fetchErr } = await supabase
      .from('initiatives')
      .select('id, plan_version_id')
      .eq('id', initiativeId)
      .single();
    if (fetchErr || !init) {
      return { data: null, error: fetchErr?.message ?? 'Initiative not found' };
    }

    const updatePayload: Record<string, unknown> = { status };
    if (status === 'dropped') {
      updatePayload.drop_reason = options!.drop_reason;
    } else {
      updatePayload.drop_reason = null;
    }

    const { data: updated, error } = await supabase
      .from('initiatives')
      .update(updatePayload)
      .eq('id', initiativeId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };

    if (status === 'dropped') {
      const { data: pv } = await supabase
        .from('plan_versions')
        .select('plan_cycle_id')
        .eq('id', init.plan_version_id)
        .single();
      await recordPlanningAudit({
        event_type: 'InitiativeDropped',
        plan_cycle_id: pv?.plan_cycle_id ?? undefined,
        plan_version_id: init.plan_version_id,
        actor_id: actorId,
        reason: options?.drop_reason ?? null,
        payload: { initiative_id: initiativeId, reason: options?.drop_reason },
      });
    }

    return { data: updated as Initiative, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to update initiative status' };
  }
}

/** Option for Planning input selector (published list or item pool). */
export interface PlanningInputOption {
  id: string;
  name: string;
  itemCount: number;
  tags?: string[];
  /**
   * Governance state of the underlying artifact. `ratified` is a real terminal
   * state — governance-complete but not yet the Plan of Record — and used to be
   * invisible to every planning consumer (finding F15). Callers badge it so the
   * user knows a ratified list is plannable but not yet published.
   */
  state?: 'ratified' | 'published';
  /** When the artifact reached its current state — newest-first ordering. */
  decidedAt?: string | null;
}

/**
 * List published prioritization artifacts available as Planning input (canon 10).
 * Returns id, name, and item count for dropdown/selector. Scoped by workspace when tenant context exists.
 */
export async function listPublishedPriorityLists(): Promise<{
  data: PlanningInputOption[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = getTenantContext()?.workspaceId ?? getCurrentWorkspaceId() ?? null;
    if (!workspaceId) return { data: [], error: null };
    const { data, error } = await supabase
      .from('priority_sets')
      .select('id, name, ranked_item_ids, item_ids, item_pool_ids, state, published_at, ratified_at, track_id, session_id')
      // F15: a ratified set is governance-complete and plannable. Filtering on
      // 'published' alone made a just-ratified list invisible to planning —
      // the ratified-but-unpublished dead zone.
      .in('state', ['ratified', 'published'])
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false })
      .limit(100);
    if (error) return { data: [], error: error.message };

    const allPrioritySetRows = (data ?? []) as Array<{
      id: string;
      name: string;
      ranked_item_ids?: string[];
      item_ids?: string[];
      item_pool_ids?: string[];
      state?: string;
      published_at?: string | null;
      ratified_at?: string | null;
      track_id?: string | null;
      session_id?: string | null;
    }>;

    // S-22 (mig 892): a track's published result is the one that feeds the
    // Plan of Record. A set made from another session on a track that has a
    // published result is left out; sets with no track are unchanged.
    const published = await fetchPublishedTrackResults(
      allPrioritySetRows.map((r) => r.track_id ?? '').filter(Boolean),
    );
    const prioritySetRows = allPrioritySetRows.filter((r) => {
      if (!r.track_id || !r.session_id) return true;
      const p = published.get(r.track_id);
      return !p?.sessionId || p.sessionId === r.session_id;
    });

    const itemPoolIds = Array.from(
      new Set(prioritySetRows.flatMap((row) => row.item_pool_ids ?? []).filter(Boolean))
    );

    let tagsByPoolId = new Map<string, string[]>();
    if (itemPoolIds.length > 0) {
      const { data: itemPools, error: itemPoolsError } = await supabase
        .from('item_pools')
        .select('id, tags')
        .in('id', itemPoolIds);

      if (itemPoolsError) {
        return { data: [], error: itemPoolsError.message };
      }

      tagsByPoolId = new Map(
        ((itemPools ?? []) as Array<{ id: string; tags?: string[] }>).map((pool) => [
          pool.id,
          pool.tags?.filter(Boolean) ?? [],
        ])
      );
    }

    const options: PlanningInputOption[] = prioritySetRows.map((row) => {
      const ids = (row.ranked_item_ids && row.ranked_item_ids.length > 0)
        ? row.ranked_item_ids
        : row.item_ids ?? [];

      const tags = derivePlanningInputTags(row.item_pool_ids, tagsByPoolId);

      return {
        id: row.id,
        name: row.name || `List ${row.id.slice(0, 8)}`,
        itemCount: ids.filter(Boolean).length,
        tags,
        state: row.state === 'ratified' ? ('ratified' as const) : ('published' as const),
        decidedAt: row.published_at ?? row.ratified_at ?? null,
      };
    });

    // Also surface ratified priority_proposals as planning inputs.
    // The governance pipeline stamps leadership_approved on items directly;
    // these proposals carry the ranked list and are valid planning inputs.
    try {
      const proposalQuery = supabase
        .from('priority_proposals')
        .select('id, title, ranked_items, status, cycle_id')
        .in('status', ['ratified', 'published'])
        .order('lifted_at', { ascending: false })
        .limit(50);
      if (workspaceId) proposalQuery.eq('workspace_id', workspaceId);
      const { data: proposals } = await proposalQuery;
      if (proposals) {
        const existingIds = new Set(options.map((o) => o.id));
        for (const p of proposals as Array<{ id: string; title: string; ranked_items: Array<{ itemId?: string }>; status: string; cycle_id: string }>) {
          if (existingIds.has(p.id)) continue;
          const itemCount = (p.ranked_items ?? []).filter((i) => i?.itemId).length;
          options.push({
            id: p.id,
            name: p.title || `Proposal ${p.id.slice(0, 8)}`,
            itemCount,
            tags: ['governance-proposal'],
            state: p.status === 'ratified' ? 'ratified' : 'published',
            decidedAt: null,
          });
        }
      }
    } catch {
      // Non-fatal: priority_proposals may not exist in all environments
    }

    return { data: options, error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load published lists' };
  }
}

/**
 * List item pools available as Planning input (canon 10).
 * Returns id, name, and item count for dropdown/selector. Scoped by workspace when tenant context exists.
 */
/** List item pools (prioritization) as planning input options. Read-only; does not modify item_pools or ranking. */
export async function listItemPools(): Promise<{
  data: PlanningInputOption[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = getTenantContext()?.workspaceId ?? getCurrentWorkspaceId() ?? null;
    if (!workspaceId) return { data: [], error: null };
    const { data, error } = await supabase
      .from('item_pools')
      .select('id, name, priority_ids, tags')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false })
      .limit(100);
    if (error) return { data: [], error: error.message };
    const options: PlanningInputOption[] = (data ?? []).map((row: { id: string; name: string; priority_ids?: string[]; tags?: string[] }) => {
      const ids = row.priority_ids ?? [];
      return {
        id: row.id,
        name: row.name || `Pool ${row.id.slice(0, 8)}`,
        itemCount: ids.filter(Boolean).length,
        tags: row.tags?.filter(Boolean) ?? [],
      };
    });
    return { data: options, error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load item pools' };
  }
}

/**
 * List workspace collections for use as a plan input source.
 */
export async function listCollectionsForPlanning(): Promise<{
  data: PlanningInputOption[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = getTenantContext()?.workspaceId ?? getCurrentWorkspaceId() ?? null;
    if (!workspaceId) return { data: [], error: null };
    const [{ data: cols, error: colErr }, { data: mems, error: memErr }] = await Promise.all([
      supabase
        .from('collections')
        .select('id, name')
        .eq('workspace_id', workspaceId)
        .order('name', { ascending: true })
        .limit(200),
      supabase
        .from('collection_memberships')
        .select('collection_id')
        .eq('workspace_id', workspaceId),
    ]);
    if (colErr) return { data: [], error: colErr.message };
    if (memErr) return { data: [], error: memErr.message };

    const countMap: Record<string, number> = {};
    for (const m of mems ?? []) {
      countMap[m.collection_id] = (countMap[m.collection_id] ?? 0) + 1;
    }
    const options: PlanningInputOption[] = (cols ?? []).map((c: { id: string; name: string }) => ({
      id: c.id,
      name: c.name || `Collection ${c.id.slice(0, 8)}`,
      itemCount: countMap[c.id] ?? 0,
      tags: [],
    }));
    return { data: options, error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load collections' };
  }
}

/**
 * List org-unit Queues (intake landing spots) for Planning Engine Input Scope.
 * A Queue is an org unit with queue_settings.enabled = true.
 */
export async function listQueuesForPlanning(): Promise<{
  data: PlanningInputOption[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = getTenantContext()?.workspaceId ?? getCurrentWorkspaceId() ?? null;
    if (!workspaceId) return { data: [], error: null };

    const { data: units, error: unitErr } = await supabase
      .from('org_units')
      .select('id, display_name, name, queue_settings')
      .eq('workspace_id', workspaceId)
      .limit(300);
    if (unitErr) return { data: [], error: unitErr.message };

    const queues = ((units ?? []) as Array<{
      id: string;
      display_name?: string | null;
      name?: string | null;
      queue_settings?: { enabled?: boolean } | null;
    }>).filter((u) => u.queue_settings?.enabled === true);

    if (queues.length === 0) return { data: [], error: null };

    const queueIds = queues.map((q) => q.id);
    const { data: itemRows, error: itemErr } = await supabase
      .from('items')
      .select('queue_id')
      .in('queue_id', queueIds);
    if (itemErr) return { data: [], error: itemErr.message };

    const countMap: Record<string, number> = {};
    for (const row of (itemRows ?? []) as { queue_id: string }[]) {
      if (!row.queue_id) continue;
      countMap[row.queue_id] = (countMap[row.queue_id] ?? 0) + 1;
    }

    const options: PlanningInputOption[] = queues.map((q) => ({
      id: q.id,
      name: q.display_name || q.name || `Queue ${q.id.slice(0, 8)}`,
      itemCount: countMap[q.id] ?? 0,
      tags: [],
    }));
    options.sort((a, b) => a.name.localeCompare(b.name));
    return { data: options, error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load queues' };
  }
}

/**
 * Get ordered item IDs from the most recently closed cycle's carry-forward decisions (Doc 13 §6).
 * Returns item ids in initiative priority order for the new cycle input; sourceCycleId is stored as input_reference_id.
 */
export async function getCarryForwardItemIdsFromLastClosedCycle(): Promise<{
  itemIds: string[];
  sourceCycleId: string | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = getTenantContext()?.workspaceId ?? getCurrentWorkspaceId() ?? null;
    let q = supabase
      .from(TABLE_PLAN_CYCLES)
      .select('id')
      .eq('status', 'closed')
      .not('closed_at', 'is', null)
      .order('closed_at', { ascending: false })
      .limit(1);
    if (workspaceId) q = q.eq('workspace_id', workspaceId);
    const { data: closedRows, error: cycleErr } = await q;
    if (cycleErr || !closedRows?.length) {
      return { itemIds: [], sourceCycleId: null, error: closedRows?.length === 0 ? null : (cycleErr?.message ?? 'No closed cycle') };
    }
    const sourceCycleId = (closedRows[0] as { id: string }).id;

    const { data: porRows } = await supabase
      .from(TABLE_PLAN_OF_RECORD)
      .select('plan_version_id')
      .eq('plan_cycle_id', sourceCycleId)
      .order('authorized_at', { ascending: false })
      .limit(1);
    const planVersionId = porRows?.[0] ? (porRows[0] as { plan_version_id: string }).plan_version_id : null;
    if (!planVersionId) return { itemIds: [], sourceCycleId, error: null };

    const { data: initRows } = await supabase
      .from('initiatives')
      .select('id, priority_position')
      .eq('plan_version_id', planVersionId)
      .order('priority_position', { ascending: true, nullsFirst: false });
    const initiatives = (initRows ?? []) as { id: string; priority_position: number | null }[];

    const { data: decRows, error: decErr } = await supabase
      .from('plan_cycle_closure_decisions')
      .select('initiative_id, item_id')
      .eq('plan_cycle_id', sourceCycleId)
      .eq('decision', 'carry_forward');
    if (decErr) return { itemIds: [], sourceCycleId, error: decErr.message };
    const carrySet = new Set((decRows ?? []).map((r: { initiative_id: string }) => r.initiative_id));
    const decByInit = new Map((decRows ?? []).map((r: { initiative_id: string; item_id: string }) => [r.initiative_id, r.item_id]));

    const itemIds: string[] = [];
    for (const init of initiatives) {
      if (!carrySet.has(init.id)) continue;
      const itemId = decByInit.get(init.id);
      if (itemId) itemIds.push(itemId);
    }
    return { itemIds, sourceCycleId, error: null };
  } catch (e) {
    return {
      itemIds: [],
      sourceCycleId: null,
      error: e instanceof Error ? e.message : 'Failed to get carry-forward from last closed cycle',
    };
  }
}

/**
 * Resolve item IDs and optional order from Planning input (canon 13 §2).
 * Returns ordered item ids for initiative creation; empty if input missing or invalid.
 * Read-only: reads from priority_sets or item_pools; does not modify prioritization state.
 */
export async function resolveInputItemIds(
  inputType: 'published_priority_list' | 'item_pool' | 'collection',
  inputReferenceId: string
): Promise<{ itemIds: string[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    if (inputType === 'published_priority_list') {
      // First try legacy priority_sets table
      const { data, error } = await supabase
        .from('priority_sets')
        .select('ranked_item_ids, item_ids')
        .eq('id', inputReferenceId)
        .maybeSingle();
      if (error) return { itemIds: [], error: error.message };
      if (data) {
        const ids = (data.ranked_item_ids && data.ranked_item_ids.length > 0)
          ? (data.ranked_item_ids as string[])
          : (data.item_ids ?? []) as string[];
        return { itemIds: ids.filter(Boolean), error: null };
      }

      // Fall back to priority_proposals (governance pipeline output)
      const { data: proposal, error: propError } = await supabase
        .from('priority_proposals')
        .select('ranked_items')
        .eq('id', inputReferenceId)
        .in('status', ['ratified', 'published'])
        .maybeSingle();
      if (propError) return { itemIds: [], error: propError.message };
      if (!proposal) return { itemIds: [], error: 'Published priority list not found' };
      const rankedItems = (proposal as { ranked_items?: Array<{ itemId?: string }> }).ranked_items ?? [];
      const ids = rankedItems.map((i) => i?.itemId).filter(Boolean) as string[];
      return { itemIds: ids, error: null };
    }
    if (inputType === 'item_pool') {
      const { data, error } = await supabase
        .from('item_pools')
        .select('priority_ids')
        .eq('id', inputReferenceId)
        .maybeSingle();
      if (error) return { itemIds: [], error: error.message };
      if (!data) return { itemIds: [], error: 'Item pool not found' };
      const ids = ((data as { priority_ids?: string[] }).priority_ids ?? []) as string[];
      return { itemIds: ids.filter(Boolean), error: null };
    }
    if (inputType === 'collection') {
      const { data, error } = await supabase
        .from('collection_memberships')
        .select('item_id')
        .eq('collection_id', inputReferenceId);
      if (error) return { itemIds: [], error: error.message };
      if (!data || data.length === 0) return { itemIds: [], error: 'Collection is empty or not found' };
      const ids = (data as { item_id: string }[]).map((m) => m.item_id).filter(Boolean);
      return { itemIds: ids, error: null };
    }
    return { itemIds: [], error: 'Unknown input type' };
  } catch (e) {
    return { itemIds: [], error: e instanceof Error ? e.message : 'Failed to resolve input' };
  }
}

// ---------------------------------------------------------------------------
// Phase 1 orchestration (Doc 13 Steps 0–2). Payload is canonical; initiatives
// and work_units are derived from payload in the same operation.
// ---------------------------------------------------------------------------

export type PlanningInputTypeSnapshot = 'published_priority_list' | 'item_pool' | 'collection' | 'previous_carry_forward';

/**
 * Load a read-only planning input snapshot from prioritization (canon 10 §3, 13 Step 0).
 * Does not mutate prioritization tables.
 */
export async function loadPlanningInputSnapshot(
  inputType: PlanningInputTypeSnapshot,
  inputReferenceId: string | null,
  _options?: { input_snapshot_at?: string | null; input_version_id?: string | null }
): Promise<{ data: PlanningInputSnapshot | null; error: string | null }> {
  try {
    let itemIds: string[];
    let resolvedInputReferenceId: string | null;

    if (inputType === 'previous_carry_forward') {
      const carry = await getCarryForwardItemIdsFromLastClosedCycle();
      if (carry.error && carry.itemIds.length === 0) {
        return { data: null, error: carry.error };
      }
      itemIds = carry.itemIds;
      resolvedInputReferenceId = carry.sourceCycleId ?? inputReferenceId ?? NIL_UUID;
    } else {
      if (!inputReferenceId) {
        return { data: null, error: 'A planning input must be selected before creating a plan cycle.' };
      }
      const resolved = await resolveInputItemIds(inputType, inputReferenceId);
      if (resolved.error && resolved.itemIds.length === 0) {
        return { data: null, error: resolved.error };
      }
      itemIds = resolved.itemIds;
      resolvedInputReferenceId = inputReferenceId;
    }

    const { data: titleMap } = await getItemTitlesByIds(itemIds);
    return {
      data: {
        itemIds,
        inputReferenceId: resolvedInputReferenceId,
        titleMap: titleMap ?? {},
      },
      error: null,
    };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to load planning input snapshot',
    };
  }
}

/**
 * Build the immutable plan payload from ordered item IDs (pure; no DB).
 * Canon 13 Step 2. Output is the canonical shape for plan_versions.payload;
 * initiatives/work_units tables are derived from this in the same operation.
 */
export function materializePlanPayload(
  itemIds: string[],
  titleMap: Record<string, string> = {}
): PlanOfRecordSnapshotPayload {
  const initiatives: Initiative[] = itemIds.map((item_id, index) => ({
    id: crypto.randomUUID(),
    plan_version_id: '' as string, // Filled when persisting to initiatives table; omitted in stored payload
    item_id,
    title: titleMap[item_id] ?? `Item ${item_id.slice(0, 8)}`,
    work_profile: null,
    priority_position: index,
    status: 'planned',
    drop_reason: null,
  }));

  return {
    initiatives,
    work_units: [],
    dependency_links: [],
    capacity_allocations: [],
    milestones: [],
    assumptions: [],
    risks: [],
    critical_path_work_unit_ids: [],
    readiness: [],
  };
}

/** Input for insertPlanCycle (Step 0). */
export interface InsertPlanCycleParams {
  name: string;
  input_type: PlanningInputTypeSnapshot;
  input_reference_id: string | null;
  input_version_id?: string | null;
  input_snapshot_at?: string | null;
  start_condition?: 'immediate' | 'date' | 'condition_based';
  workspace_id?: string | null;
  created_by?: string | null;
}

/**
 * Insert one plan cycle (canon 13 Step 0). No versions or initiatives.
 */
export async function insertPlanCycle(
  params: InsertPlanCycleParams
): Promise<{ data: PlanCycle | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = params.workspace_id ?? getTenantContext()?.workspaceId ?? getCurrentWorkspaceId() ?? null;

    if (workspaceId) {
      // Same-request context for planning_workspace_visible (transaction-local).
      await supabase.rpc('set_workspace_context', { wid: workspaceId });
    }

    let makePrimary = true;
    if (workspaceId) {
      const { data: existingPrimary } = await supabase
        .from(TABLE_PLAN_CYCLES)
        .select('id')
        .eq('workspace_id', workspaceId)
        .eq('is_primary', true)
        .limit(1)
        .maybeSingle();
      makePrimary = !existingPrimary;
    }

    const { data, error } = await supabase
      .from(TABLE_PLAN_CYCLES)
      .insert({
        name: params.name,
        input_type: params.input_type,
        input_reference_id: params.input_reference_id,
        input_version_id: params.input_version_id ?? null,
        input_snapshot_at: params.input_snapshot_at ?? null,
        start_condition: params.start_condition ?? 'immediate',
        status: 'draft',
        workspace_id: workspaceId,
        created_by: params.created_by ?? null,
        is_primary: makePrimary,
      })
      .select('*')
      .single();
    if (error) {
      const msg = error.message ?? '';
      if (/row-level security/i.test(msg) || error.code === '42501') {
        return {
          data: null,
          error:
            'Could not create the plan cycle — the Planning module may be off for this workspace, or you lack access. Enable Planning in workspace settings and try again.',
        };
      }
      return { data: null, error: msg };
    }
    await recordPlanningAudit({
      event_type: 'PlanCycleCreated',
      plan_cycle_id: (data as PlanCycle).id,
      actor_id: params.created_by ?? null,
      reason: 'insertPlanCycle',
    });
    return { data: data as PlanCycle, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to insert plan cycle' };
  }
}

/** Input for insertPlanVersion (Step 1 + 2). Payload is canonical; supporting rows derived from it. */
export interface InsertPlanVersionParams {
  plan_cycle_id: string;
  version_number: number;
  created_by: string | null;
  change_reason: PlanVersionChangeReason | null;
  payload: PlanOfRecordSnapshotPayload;
}

const RPC_CREATE_PLAN_VERSION_WITH_INITIATIVES = 'create_plan_version_with_initiatives';

/**
 * Insert one plan version with payload and derive initiatives from that payload (canon 13 Step 1–2).
 * Uses RPC for atomicity: plan_versions + initiatives + audit in one transaction.
 * plan_versions.payload is the canonical source; initiatives table is supporting.
 */
export async function insertPlanVersion(
  params: InsertPlanVersionParams
): Promise<{ data: PlanVersion | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const initiativesInPayload = params.payload.initiatives as Initiative[];
    const payloadForDb: Record<string, unknown> = {
      ...params.payload,
      initiatives: initiativesInPayload.map(({ plan_version_id: _pv, ...rest }) => rest),
    };

    const { data: versionRow, error: rpcError } = await supabase.rpc(RPC_CREATE_PLAN_VERSION_WITH_INITIATIVES, {
      p_plan_cycle_id: params.plan_cycle_id,
      p_version_number: params.version_number,
      p_payload: payloadForDb,
      p_change_reason: params.change_reason,
      p_created_by: params.created_by,
      p_engine_version: ENGINE_VERSION,
    });

    if (rpcError) {
      return { data: null, error: rpcError.message };
    }
    if (versionRow == null) {
      return { data: null, error: 'RPC returned no version row' };
    }
    return { data: versionRow as unknown as PlanVersion, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to insert plan version' };
  }
}

/**
 * Build Phase 1 DTO from persisted plan cycle and plan version (read-only).
 */
export async function buildPlanningDto(
  planCycleId: string,
  planVersionId: string
): Promise<{ data: Phase1PlanningDto | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const [cycleRes, versionRes] = await Promise.all([
      supabase.from(TABLE_PLAN_CYCLES).select('*').eq('id', planCycleId).single(),
      supabase.from(TABLE_PLAN_VERSIONS).select('*').eq('id', planVersionId).single(),
    ]);
    const cycle = cycleRes.data as PlanCycle | null;
    const version = versionRes.data as (PlanVersion & { payload?: PlanOfRecordSnapshotPayload }) | null;
    if (cycleRes.error || !cycle) return { data: null, error: cycleRes.error?.message ?? 'Plan cycle not found' };
    if (versionRes.error || !version) return { data: null, error: versionRes.error?.message ?? 'Plan version not found' };
    if ((version as { plan_cycle_id?: string }).plan_cycle_id !== planCycleId) {
      return { data: null, error: 'Plan version does not belong to this plan cycle' };
    }
    const raw = version.payload ?? { initiatives: [], work_units: [], dependency_links: [], capacity_allocations: [], milestones: [], assumptions: [], risks: [], critical_path_work_unit_ids: [], readiness: [] } as PlanOfRecordSnapshotPayload;
    const payload: PlanOfRecordSnapshotPayload = {
      ...raw,
      initiatives: (raw.initiatives ?? []).map((i: Initiative & { plan_version_id?: string }) => ({
        ...i,
        plan_version_id: i.plan_version_id ?? planVersionId,
      })) as Initiative[],
    };
    return {
      data: { planCycle: cycle, planVersion: version as PlanVersion, payload },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to build planning DTO' };
  }
}

/**
 * Phase 1: Create plan cycle + initial plan version and return DTO (canon 13 Steps 0–2).
 * Does not create POR or run constraint evaluation. Payload is canonical; initiatives derived from it.
 */
export async function createPlanCycleWithInitialVersion(
  input: CreatePlanCycleInput
): Promise<{ data: Phase1PlanningDto | null; error: string | null }> {
  try {
    const createdBy = input.created_by ?? getTenantContext()?.userId ?? null;
    const inputRef = input.input_reference_id ?? null;

    const { data: snapshot, error: snapErr } = await loadPlanningInputSnapshot(
      input.input_type as PlanningInputTypeSnapshot,
      inputRef,
      { input_snapshot_at: input.input_snapshot_at, input_version_id: input.input_version_id }
    );
    if (snapErr || !snapshot) return { data: null, error: snapErr ?? 'Failed to load input snapshot' };

    const payload = materializePlanPayload(snapshot.itemIds, snapshot.titleMap);

    const { data: cycle, error: cycleErr } = await insertPlanCycle({
      name: input.name,
      input_type: input.input_type as PlanningInputTypeSnapshot,
      input_reference_id: snapshot.inputReferenceId,
      input_version_id: input.input_version_id ?? null,
      input_snapshot_at: input.input_snapshot_at ?? null,
      start_condition: input.start_condition ?? 'immediate',
      created_by: createdBy,
    });
    if (cycleErr || !cycle) return { data: null, error: cycleErr ?? 'Failed to insert plan cycle' };

    const { data: version, error: versionErr } = await insertPlanVersion({
      plan_cycle_id: cycle.id,
      version_number: 0,
      created_by: createdBy,
      change_reason: null,
      payload,
    });
    if (versionErr || !version) return { data: null, error: versionErr ?? 'Failed to insert plan version' };

    return buildPlanningDto(cycle.id, version.id);
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create plan cycle with initial version' };
  }
}

/**
 * Canonical creation path: create plan cycle + initial plan version (atomic RPC) + POR + snapshot.
 * Uses loadPlanningInputSnapshot → materializePlanPayload → insertPlanCycle → insertPlanVersion (RPC)
 * → insert plan_of_record → createPlanOfRecordSnapshot. If snapshot creation fails, the operation
 * fails and an error is returned (no partial success). Canon 13 Steps 0–2 + initial POR (Doc 11).
 */
export async function createPlanCycleWithPor(
  input: CreatePlanCycleInput
): Promise<{ data: { planCycle: PlanCycle; planVersion: PlanVersion; planOfRecord: PlanOfRecord } | null; error: string | null }> {
  try {
    const createdBy = input.created_by ?? getTenantContext()?.userId ?? null;
    const inputRef = input.input_reference_id ?? null;

    const { data: snapshot, error: snapErr } = await loadPlanningInputSnapshot(
      input.input_type as PlanningInputTypeSnapshot,
      inputRef,
      { input_snapshot_at: input.input_snapshot_at, input_version_id: input.input_version_id }
    );
    if (snapErr || !snapshot) return { data: null, error: snapErr ?? 'Failed to load input snapshot' };

    const payload = materializePlanPayload(snapshot.itemIds, snapshot.titleMap);

    const { data: cycle, error: cycleErr } = await insertPlanCycle({
      name: input.name,
      input_type: input.input_type as PlanningInputTypeSnapshot,
      input_reference_id: snapshot.inputReferenceId,
      input_version_id: input.input_version_id ?? null,
      input_snapshot_at: input.input_snapshot_at ?? null,
      start_condition: input.start_condition ?? 'immediate',
      created_by: createdBy,
    });
    if (cycleErr || !cycle) return { data: null, error: cycleErr ?? 'Failed to insert plan cycle' };

    const { data: version, error: versionErr } = await insertPlanVersion({
      plan_cycle_id: cycle.id,
      version_number: 0,
      created_by: createdBy,
      change_reason: null,
      payload,
    });
    if (versionErr || !version) return { data: null, error: versionErr ?? 'Failed to insert plan version' };

    const supabase = getSupabaseClient();
    const authorizedAt = new Date().toISOString();
    const { data: por, error: porErr } = await supabase
      .from(TABLE_PLAN_OF_RECORD)
      .insert({
        plan_cycle_id: cycle.id,
        plan_version_id: version.id,
        authorized_at: authorizedAt,
        authorized_by: createdBy,
        status: 'active',
      })
      .select('*')
      .single();

    if (porErr || !por) {
      return { data: null, error: porErr?.message ?? 'Failed to create plan of record' };
    }

    const snapshotResult = await createPlanOfRecordSnapshot(por.id);
    if (snapshotResult.error || !snapshotResult.data) {
      return { data: null, error: snapshotResult.error ?? 'Failed to create POR snapshot' };
    }

    return {
      data: {
        planCycle: cycle,
        planVersion: version,
        planOfRecord: por as PlanOfRecord,
      },
      error: null,
    };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to create plan cycle with POR',
    };
  }
}

// ---------------------------------------------------------------------------

/**
 * New plan version from the active plan of record — ONE server transaction
 * (create_plan_version_from_current, migration 764).
 *
 * plan_versions is immutable (078 trigger: no UPDATE, no DELETE), so the
 * version must be complete in its INSERT. The old client flow created an
 * empty-payload row, copied children, then tried to UPDATE the payload and
 * source decision — which the trigger always refused, leaving an orphan
 * version behind. The RPC builds the payload from the copies first.
 *
 *   mode 'replan' — Propose replan: copies the plan of record's initiatives.
 *   mode 'draft'  — Create draft: full structural clone (initiatives, work
 *                   units, dependency links, capacity allocations).
 *
 * Authority: a member of the cycle's workspace (706); the cycle must be draft
 * or active and have a plan of record. Creator = the signed-in person.
 */
async function createPlanVersionFromCurrentRpc(params: {
  planCycleId: string;
  changeReason: PlanVersionChangeReason;
  sourceDecisionId?: string | null;
  mode: 'replan' | 'draft';
}): Promise<{ data: PlanVersion | null; error: string | null }> {
  try {
    const { data, error } = await getSupabaseClient().rpc('create_plan_version_from_current', {
      p_plan_cycle_id: params.planCycleId,
      p_change_reason: params.changeReason,
      p_source_decision_id: params.sourceDecisionId ?? null,
      p_mode: params.mode,
    });
    if (error) return { data: null, error: error.message };
    const res = (data ?? {}) as { success?: boolean; error?: string; plan_version?: PlanVersion };
    if (!res.success || !res.plan_version) {
      return { data: null, error: res.error ?? 'No plan version was created' };
    }
    return { data: res.plan_version, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'No plan version was created' };
  }
}

/**
 * Create a new plan version from the current POR (replan). Copies initiatives from current version.
 * Does not copy work_units/dependencies; use for "propose replan" flow.
 */
export async function createPlanVersionFromCurrent(
  planCycleId: string,
  changeReason: PlanVersionChangeReason,
  opts?: {
    /** The decision this replan carries out (change-course answer) — recorded as the version's why. */
    sourceDecisionId?: string | null;
  }
): Promise<{ data: PlanVersion | null; error: string | null }> {
  return createPlanVersionFromCurrentRpc({
    planCycleId,
    changeReason,
    sourceDecisionId: opts?.sourceDecisionId ?? null,
    mode: 'replan',
  });
}

/**
 * Create a draft plan version by full structural clone of current POR (Doc 12, 13 §5).
 * Copies initiatives, work_units, dependency_links, capacity_allocations. All edits then go to this draft until Authorize POR.
 */
export async function createDraftPlanVersion(planCycleId: string): Promise<{
  data: PlanVersion | null;
  error: string | null;
}> {
  return createPlanVersionFromCurrentRpc({ planCycleId, changeReason: 'scope_adjustment', mode: 'draft' });
}


/**
 * Draft PlanVersion id for this cycle if one exists (latest non-POR version). Doc 12, 13 §5.
 */
export async function getDraftPlanVersionId(planCycleId: string): Promise<{ draftPlanVersionId: string | null; error: string | null }> {
  try {
    const { data: por, error: porErr } = await getActivePlanOfRecord(planCycleId);
    if (porErr || !por) return { draftPlanVersionId: null, error: porErr };
    const porVersionId = por.plan_version_id as string;
    const supabase = getSupabaseClient();
    const { data: versions, error: vErr } = await supabase
      .from(TABLE_PLAN_VERSIONS)
      .select('id')
      .eq('plan_cycle_id', planCycleId)
      .order('created_at', { ascending: false });
    if (vErr || !versions?.length) return { draftPlanVersionId: null, error: vErr?.message ?? null };
    const draft = (versions as { id: string }[]).find((v) => v.id !== porVersionId);
    return { draftPlanVersionId: draft?.id ?? null, error: null };
  } catch (e) {
    return { draftPlanVersionId: null, error: e instanceof Error ? e.message : 'Failed to get draft version' };
  }
}

/**
 * Plan version to use for editing (draft if exists, else current POR). Use for plan detail / work graph when cycleId is known.
 */
export async function getEditingPlanVersionIdForCycle(planCycleId: string): Promise<{
  planVersionId: string;
  editingPlanVersionId: string;
  draftPlanVersionId: string | null;
  error: string | null;
}> {
  const { data: por, error: porErr } = await getActivePlanOfRecord(planCycleId);
  if (porErr || !por) {
    return {
      planVersionId: '',
      editingPlanVersionId: '',
      draftPlanVersionId: null,
      error: porErr ?? 'No active POR',
    };
  }
  const planVersionId = por.plan_version_id as string;
  const { draftPlanVersionId } = await getDraftPlanVersionId(planCycleId);
  const editingPlanVersionId = draftPlanVersionId ?? planVersionId;
  return { planVersionId, editingPlanVersionId, draftPlanVersionId, error: null };
}

/**
 * Run POR authorization gate check (Doc 12 §4.1, Doc 15 §3.1, §6 truth table).
 * Returns allowed + errors for UI. Use before authorizeNewPlanOfRecord.
 * Verifies plan version belongs to cycle, then evaluates Doc 15 predicates.
 * relaxLegacy: only set true when caller is allowed legacy bypass (legacy cycle + Approver/Admin). Default false.
 */
export async function runPorAuthorizationGateCheck(
  planCycleId: string,
  planVersionId: string,
  options?: { includePredicates?: boolean; relaxLegacy?: boolean }
): Promise<{ data: PorAuthorizableResult | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data: pv, error: pvErr } = await supabase
      .from(TABLE_PLAN_VERSIONS)
      .select('id, plan_cycle_id')
      .eq('id', planVersionId)
      .single();
    if (pvErr || !pv) return { data: null, error: pvErr?.message ?? 'Plan version not found' };
    if ((pv as { plan_cycle_id: string }).plan_cycle_id !== planCycleId) {
      return { data: null, error: 'Plan version does not belong to this cycle' };
    }
    const relaxLegacy = options?.relaxLegacy === true;
    const result = await porAuthorizable(planVersionId, {
      includePredicates: options?.includePredicates,
      relaxBucketsAndEvaluationForLegacy: relaxLegacy,
    });
    return { data: result, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Gate check failed',
    };
  }
}

/**
 * Authorize a plan version as the new Plan of Record (canon 10, 11).
 * Uses Doc 15 gate check then authorize_por RPC; authoritative POR is plan_of_record_snapshots.
 * Gate bypass (relax buckets/evaluation) only when LegacyPlanCycle and caller is Approver/Admin.
 * No skipGateCheck or relax flags in public API; normal runtime always runs gate.
 */
/**
 * Every plan-of-record row the cycle has had, oldest first, with the moment
 * each superseded one stopped being the plan of record (the next row's
 * authorized_at — the table keeps no superseded_at of its own). Lets the
 * governance list say "plan of record 24 Sep – 7 Oct" on a row instead of
 * offering to authorize it again (build contract §2.8).
 */
export async function listPlanOfRecordHistory(planCycleId: string): Promise<{
  data: Array<{ plan_version_id: string; status: string; authorized_at: string | null; superseded_at: string | null }>;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_OF_RECORD)
      .select('plan_version_id, status, authorized_at')
      .eq('plan_cycle_id', planCycleId)
      .order('authorized_at', { ascending: true });
    if (error) return { data: [], error: error.message };
    const rows = (data ?? []) as Array<{ plan_version_id: string; status: string; authorized_at: string | null }>;
    return {
      data: rows.map((r, i) => ({
        ...r,
        superseded_at: r.status === 'active' ? null : rows[i + 1]?.authorized_at ?? null,
      })),
      error: null,
    };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load plan of record history' };
  }
}

/**
 * What authorizing this version would run into — read before the dialog
 * opens, so the refusal is on the screen before the click, with the items
 * named (build contract §2.8). The same gate `authorizeNewPlanOfRecord` runs,
 * minus the two mechanical prerequisites it provisions by itself (buckets,
 * evaluation), which are not the person's to fix.
 */
export async function previewPorAuthorization(
  planCycleId: string,
  planVersionId: string,
): Promise<{ data: { ready: boolean; blockers: PorBlocker[] } | null; error: string | null }> {
  // Divergences are recorded here so they can be settled in this dialog,
  // before the click that would otherwise be refused.
  const divergences = await runAndPersistDivergences(planVersionId);
  if (divergences.error) return { data: null, error: divergences.error };
  const relaxLegacy = await canBypassPorGateForLegacy(planCycleId);
  const gate = await runPorAuthorizationGateCheck(planCycleId, planVersionId, { relaxLegacy });
  if (gate.error || !gate.data) return { data: null, error: gate.error ?? 'Gate check failed' };
  const blockers = gate.data.blockers.filter(
    (b) => b.blocker_code !== 'BUCKETS_NOT_MATERIALIZED' && b.blocker_code !== 'EVALUATION_NOT_COMPUTED',
  );
  return { data: { ready: blockers.length === 0, blockers }, error: null };
}

/**
 * Which of these items will stop a plan version from being authorized because
 * they carry no work type (the recipe gate, RECIPE_RESOLVED). Asked on the
 * engine summary before "Propose", so the person hears it
 * where they can still act (build contract §2.8, form 4).
 */
export async function itemsMissingWorkType(
  itemIds: readonly string[],
): Promise<{ data: Array<{ id: string; title: string }>; error: string | null }> {
  const ids = Array.from(new Set(itemIds.filter(Boolean)));
  if (ids.length === 0) return { data: [], error: null };
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('items')
      .select('id, title')
      .in('id', ids)
      .is('work_type', null);
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as Array<{ id: string; title: string }>, error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to read work types' };
  }
}

export async function authorizeNewPlanOfRecord(
  planCycleId: string,
  planVersionId: string,
  options?: { created_by?: string | null; skipRoleCheck?: boolean }
): Promise<{ data: ActivePlanOfRecordResult | null; error: string | null }> {
  try {
    // Authorizing POR requires workspace admin or owner access
    if (!options?.skipRoleCheck) {
      const roleError = await assertPlanningApprover(planCycleId);
      if (roleError) return { data: null, error: roleError };
    }

    // Self-provision the MECHANICAL Doc 15 prerequisites before gating. A plan
    // version whose buckets/evaluation were never materialized (e.g. an older
    // version authorized straight from Governance — Plan Health's Run engine
    // only operates on the latest version) must not dead-end on
    // "No capability buckets materialized"; governance records, buckets and
    // the constraint evaluation are derivable and idempotent. Only the HUMAN
    // decisions stay manual: the gate below still demands an override
    // artifact when the evaluation carries severe violations.
    const [bucketsOk, evalOk] = await Promise.all([
      bucketsMaterialized(planVersionId),
      evaluationComputed(planVersionId),
    ]);
    if (!bucketsOk.value || !evalOk.value) {
      const provision = await runPlanVersionEvaluation(planVersionId, {
        evaluated_by: getTenantContext()?.userId ?? null,
      });
      if (provision.error) return { data: null, error: provision.error };
    }

    const divergences = await runAndPersistDivergences(planVersionId);
    if (divergences.error) return { data: null, error: divergences.error };

    const allowLegacyBypass = await canBypassPorGateForLegacy(planCycleId);
    const gate = await runPorAuthorizationGateCheck(planCycleId, planVersionId, {
      relaxLegacy: allowLegacyBypass,
    });
    if (gate.error) return { data: null, error: gate.error };
    if (gate.data && !gate.data.allowed) {
      return {
        data: null,
        error: gate.data.errors.length > 0 ? gate.data.errors.join('; ') : 'POR authorization gates not satisfied',
      };
    }
    // `p_authorized_by` is a DISPLAY name — it lands in the Plan of Record's
    // authorized_by and in the Decision the authorization writes (migration
    // 788). Passing the user id put a uuid where a person's name belongs, and
    // the server's own name resolution never ran because it only fills a blank.
    // Send the name; fall back to the email; send nothing rather than an id, so
    // the server resolves it from `users` instead.
    const tenantForAuthor = getTenantContext();
    const authorizedBy =
      options?.created_by ?? tenantForAuthor?.userName ?? tenantForAuthor?.userEmail ?? null;
    const [evalRes, divRes, tradeRes, nextRightRes] = await Promise.all([
      getLatestEvaluation(planVersionId),
      listDivergencesForPlanVersion(planVersionId),
      listTradeoffsForPlanVersion(planVersionId),
      // Doc 108 §5.4: compute Next-Right scoring at POR auth time
      getNextRightWorkUnits(planCycleId, { limit: 25 }),
    ]);
    const constraint_evaluation_id = evalRes.data?.id ?? null;
    const divergence_ids = (divRes.data ?? []).map((d) => d.id);
    const tradeoff_decision_ids = (tradeRes.data ?? []).map((t) => t.id);
    const nextRightRanking = (nextRightRes.data ?? []).map((item) => ({
      work_unit_id: item.work_unit_id,
      title: item.title,
      score: item.score,
      top_positive_drivers: item.top_positive_drivers,
      top_penalties: item.top_penalties,
    }));
    const { authorizePorRpc } = await import('./planningRpcService');
    const { data: snapshotId, error: rpcError } = await authorizePorRpc({
      plan_version_id: planVersionId,
      authorized_by: authorizedBy,
      constraint_evaluation_id,
      divergence_ids: divergence_ids.length > 0 ? divergence_ids : null,
      tradeoff_decision_ids: tradeoff_decision_ids.length > 0 ? tradeoff_decision_ids : null,
      next_right_ranking: nextRightRanking.length > 0 ? nextRightRanking : null,
    });
    if (rpcError || !snapshotId) return { data: null, error: rpcError ?? 'Authorize POR failed' };
    const { data: por, error: fetchErr } = await getActivePlanOfRecord(planCycleId);
    if (fetchErr || !por) return { data: null, error: fetchErr ?? 'POR authorized but could not load active snapshot' };

    // Emit POR_AUTHORIZED audit event (Canon Doc 14 §4)
    await recordPlanningAudit({
      event_type: 'POR_AUTHORIZED',
      plan_cycle_id: planCycleId,
      plan_version_id: planVersionId,
      actor_id: authorizedBy,
      reason: 'POR authorization gate passed',
      payload: {
        snapshot_id: snapshotId,
        constraint_evaluation_id: constraint_evaluation_id,
        divergence_ids: divergence_ids,
        tradeoff_decision_ids: tradeoff_decision_ids,
      },
    });

    return { data: por, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to authorize plan of record' };
  }
}

/**
 * Build plan snapshot payload from DB for a plan version (canon 18).
 * Used to set plan_versions.payload and for snapshot creation.
 */
export async function buildPlanVersionPayload(
  planVersionId: string,
  planCycleId?: string | null
): Promise<{ data: PlanOfRecordSnapshotPayload | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data: initRows } = await supabase
      .from('initiatives')
      .select('*')
      .eq('plan_version_id', planVersionId);
    const initiatives = (initRows ?? []) as Initiative[];
    const initiativeIds = initiatives.map((i) => i.id);

    const { data: wuRows } = await supabase
      .from('work_units')
      .select('*')
      .in('initiative_id', initiativeIds);
    const work_units = (wuRows ?? []) as WorkUnit[];
    const wuIds = work_units.map((w) => w.id);

    const [linkRes, capRes, mileRes, assumpRes, riskRes, readinessRes, criticalPathRes, nextRightRes] = await Promise.all([
      wuIds.length > 0
        ? supabase.from('dependency_links').select('*').in('from_work_unit_id', wuIds)
        : Promise.resolve({ data: [] }),
      supabase.from('capacity_allocations').select('*').eq('plan_version_id', planVersionId),
      supabase.from('milestones').select('*').eq('plan_version_id', planVersionId),
      supabase.from('assumptions').select('*').eq('plan_version_id', planVersionId),
      supabase.from('risks').select('*').eq('plan_version_id', planVersionId),
      getWorkUnitReadiness(planVersionId),
      getCriticalPathWorkUnitIds(planVersionId),
      planCycleId ? getNextRightWorkUnits(planCycleId, { limit: 20 }) : Promise.resolve({ data: [] }),
    ]);

    const dependency_links = ((linkRes.data ?? []) as { from_work_unit_id: string; to_work_unit_id: string }[]).filter(
      (l) => wuIds.includes(l.to_work_unit_id)
    ) as DependencyLink[];
    const capacity_allocations = (capRes.data ?? []) as CapacityAllocation[];
    const milestones = (mileRes.data ?? []) as Milestone[];
    const assumptions = (assumpRes.data ?? []) as Assumption[];
    const risks = (riskRes.data ?? []) as Risk[];
    const readiness = readinessRes.data ?? [];
    const critical_path_work_unit_ids = Array.from(criticalPathRes.data ?? []);
    const next_right_ranking =
      nextRightRes.data?.map((n) => ({ work_unit_id: n.work_unit_id, title: n.title, score: n.score })) ?? [];

    const payload: PlanOfRecordSnapshotPayload = {
      initiatives,
      work_units,
      dependency_links,
      capacity_allocations,
      milestones,
      assumptions,
      risks,
      critical_path_work_unit_ids,
      readiness,
      next_right_ranking,
    };
    return { data: payload, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to build plan version payload',
    };
  }
}

/**
 * Create immutable PlanOfRecordSnapshot for reporting (Doc 16).
 * Called when a PlanOfRecord is authorized. Non-blocking failure: logs warning, does not fail caller.
 */
export async function createPlanOfRecordSnapshot(planOfRecordId: string): Promise<{
  data: PlanOfRecordSnapshot | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: por, error: porErr } = await supabase
      .from(TABLE_PLAN_OF_RECORD)
      .select('*')
      .eq('id', planOfRecordId)
      .single();
    if (porErr || !por) return { data: null, error: porErr?.message ?? 'PlanOfRecord not found' };

    const { data: cycle, error: cycleErr } = await supabase
      .from(TABLE_PLAN_CYCLES)
      .select('input_reference_id')
      .eq('id', por.plan_cycle_id)
      .single();
    if (cycleErr || !cycle) return { data: null, error: cycleErr?.message ?? 'Plan cycle not found' };

    const planVersionId = por.plan_version_id as string;
    const planCycleId = por.plan_cycle_id as string;

    const { data: initRows } = await supabase
      .from('initiatives')
      .select('*')
      .eq('plan_version_id', planVersionId);
    const initiatives = (initRows ?? []) as Initiative[];
    const initiativeIds = initiatives.map((i) => i.id);

    const { data: wuRows } = await supabase
      .from('work_units')
      .select('*')
      .in('initiative_id', initiativeIds);
    const work_units = (wuRows ?? []) as WorkUnit[];
    const wuIds = work_units.map((w) => w.id);

    const [linkRes, capRes, mileRes, assumpRes, riskRes, readinessRes, criticalPathRes, nextRightRes] = await Promise.all([
      wuIds.length > 0
        ? supabase.from('dependency_links').select('*').in('from_work_unit_id', wuIds)
        : Promise.resolve({ data: [] }),
      supabase.from('capacity_allocations').select('*').eq('plan_version_id', planVersionId),
      supabase.from('milestones').select('*').eq('plan_version_id', planVersionId),
      supabase.from('assumptions').select('*').eq('plan_version_id', planVersionId),
      supabase.from('risks').select('*').eq('plan_version_id', planVersionId),
      getWorkUnitReadiness(planVersionId),
      getCriticalPathWorkUnitIds(planVersionId),
      getNextRightWorkUnits(planCycleId, { limit: 20 }),
    ]);

    const dependency_links = ((linkRes.data ?? []) as { from_work_unit_id: string; to_work_unit_id: string }[]).filter(
      (l) => wuIds.includes(l.to_work_unit_id)
    ) as DependencyLink[];
    const capacity_allocations = (capRes.data ?? []) as CapacityAllocation[];
    const milestones = (mileRes.data ?? []) as Milestone[];
    const assumptions = (assumpRes.data ?? []) as Assumption[];
    const risks = (riskRes.data ?? []) as Risk[];
    const readiness = readinessRes.data ?? [];
    const critical_path_work_unit_ids = Array.from(criticalPathRes.data ?? []);
    const next_right_ranking =
      nextRightRes.data?.map((n) => ({ work_unit_id: n.work_unit_id, title: n.title, score: n.score })) ?? [];

    const payload: PlanOfRecordSnapshotPayload = {
      initiatives,
      work_units,
      dependency_links,
      capacity_allocations,
      milestones,
      assumptions,
      risks,
      critical_path_work_unit_ids,
      readiness,
      next_right_ranking,
    };

    const { data: snapshot, error: snapErr } = await supabase
      .from(TABLE_POR_SNAPSHOTS)
      .insert({
        plan_cycle_id: por.plan_cycle_id,
        plan_of_record_id: por.id,
        plan_version_id: planVersionId,
        input_reference_id: cycle.input_reference_id,
        authorized_at: por.authorized_at,
        authorized_by: por.authorized_by,
        superseded_by_snapshot_id: null,
        audit_event_id: null,
        payload,
        constraint_evaluation_id: null,
        divergence_ids: null,
        tradeoff_decision_ids: null,
      })
      .select('*')
      .single();

    if (snapErr) return { data: null, error: snapErr.message };
    return { data: snapshot as PlanOfRecordSnapshot, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to create snapshot',
    };
  }
}

/**
 * List snapshots for a plan cycle (reporting; Doc 16 §5). Ordered by authorized_at desc.
 */
export async function getSnapshotsForPlanCycle(planCycleId: string): Promise<{
  data: PlanOfRecordSnapshot[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_POR_SNAPSHOTS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('authorized_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as PlanOfRecordSnapshot[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list snapshots' };
  }
}

/**
 * Get a single snapshot by id (reporting).
 */
export async function getSnapshotById(snapshotId: string): Promise<{
  data: PlanOfRecordSnapshot | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_POR_SNAPSHOTS)
      .select('*')
      .eq('id', snapshotId)
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanOfRecordSnapshot, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get snapshot' };
  }
}

/**
 * Get first active plan cycle for viability planning — does NOT require an active POR.
 *
 * Returns the cycle and the best available plan version:
 *   1. The POR's plan version if an active POR exists (fully authorized state).
 *   2. The latest plan version if no POR exists (provisional state).
 *   3. null planVersionId if no plan versions exist yet (new cycle, blank slate).
 *
 * Callers should use `isProvisional = true` to show a "Provisional Plan" indicator
 * rather than blocking planning activities.
 */
export async function getFirstActiveCycleProvisional(opts?: { cycleId?: string | null }): Promise<{
  data: {
    cycle: PlanCycle;
    por: (PlanOfRecord & { plan_version?: PlanVersion }) | null;
    planVersionId: string | null;
    draftPlanVersionId?: string | null;
    editingPlanVersionId: string | null;
    /** True when there is no authorized POR — viability planning (no POR snapshot). */
    isProvisional: boolean;
    /** True when the resolved cycle is not status=active (e.g. draft). */
    isNonActiveCycle: boolean;
  } | null;
  error: string | null;
}> {
  // If a specific cycle was requested, load it directly.
  if (opts?.cycleId) {
    const { data: specificCycle, error: specErr } = await getPlanCycleById(opts.cycleId);
    if (specErr || !specificCycle) return { data: null, error: specErr ?? 'Plan cycle not found.' };
    const isNonActiveCycle = specificCycle.status !== 'active';
    const { data: por } = await getActivePlanOfRecord(specificCycle.id);
    if (por) {
      const planVersionId = por.plan_version_id as string;
      const { draftPlanVersionId } = await getDraftPlanVersionId(specificCycle.id);
      const editingPlanVersionId = draftPlanVersionId ?? planVersionId;
      return { data: { cycle: specificCycle, por, planVersionId, draftPlanVersionId, editingPlanVersionId, isProvisional: false, isNonActiveCycle }, error: null };
    }
    const { data: latestVersion } = await getLatestPlanVersion(specificCycle.id);
    return { data: { cycle: specificCycle, por: null, planVersionId: latestVersion?.id ?? null, draftPlanVersionId: null, editingPlanVersionId: latestVersion?.id ?? null, isProvisional: true, isNonActiveCycle }, error: null };
  }

  // Try active cycles first, then fall back to any cycle so users aren't dead-ended.
  const { data: cyclesActive, error: listErr } = await listPlanCycles({ status: 'active' });
  if (listErr) return { data: null, error: listErr };
  let isNonActiveCycle = false;
  let cycles = cyclesActive;
  if (!cycles?.length) {
    const fallback = await listPlanCycles();
    if (fallback.error) return { data: null, error: fallback.error };
    if (!fallback.data?.length) return { data: null, error: 'No active plan cycle found.' };
    cycles = fallback.data;
    isNonActiveCycle = true;
  }
  const cycle = cycles[0];

  // Try POR first (authorized state)
  const { data: por } = await getActivePlanOfRecord(cycle.id);
  if (por) {
    const planVersionId = por.plan_version_id as string;
    const { draftPlanVersionId } = await getDraftPlanVersionId(cycle.id);
    const editingPlanVersionId = draftPlanVersionId ?? planVersionId;
    return {
      data: { cycle, por, planVersionId, draftPlanVersionId, editingPlanVersionId, isProvisional: false, isNonActiveCycle },
      error: null,
    };
  }

  // No POR — fall back to latest plan version (viability planning)
  const { data: latestVersion } = await getLatestPlanVersion(cycle.id);
  return {
    data: {
      cycle,
      por: null,
      planVersionId: latestVersion?.id ?? null,
      draftPlanVersionId: null,
      editingPlanVersionId: latestVersion?.id ?? null,
      isProvisional: true,
      isNonActiveCycle,
    },
    error: null,
  };
}

/**
 * Get first active plan cycle and its active POR (for dashboard context). Doc 17 §4.
 * When a draft plan version exists (propose replan), returns draftPlanVersionId and editingPlanVersionId for edits.
 */
export async function getFirstActiveCycleWithPor(): Promise<{
  data: {
    cycle: PlanCycle;
    por: PlanOfRecord & { plan_version?: PlanVersion };
    planVersionId: string;
    /** When set, a draft exists; edits should go to this version until Authorize POR (Doc 12, 13 §5). */
    draftPlanVersionId?: string | null;
    /** Version to use for structural edits: draft if exists, else current POR. */
    editingPlanVersionId: string;
  } | null;
  error: string | null;
}> {
  const { data: cycles, error: listErr } = await listPlanCycles({ status: 'active' });
  if (listErr || !cycles?.length) return { data: null, error: listErr };
  const cycle = cycles[0];
  const { data: por, error: porErr } = await getActivePlanOfRecord(cycle.id);
  if (porErr || !por) return { data: null, error: porErr };
  const planVersionId = por.plan_version_id as string;
  const { draftPlanVersionId } = await getDraftPlanVersionId(cycle.id);
  const editingPlanVersionId = draftPlanVersionId ?? planVersionId;
  return {
    data: {
      cycle,
      por,
      planVersionId,
      draftPlanVersionId: draftPlanVersionId ?? undefined,
      editingPlanVersionId,
    },
    error: null,
  };
}

/** Legacy plan cycle cutoff (Step 1 planning hardening). Cycles created before this are legacy and may use gate bypass when caller is Approver/Admin. */
export const LEGACY_PLAN_CYCLE_CUTOFF = '2026-03-04T00:00:00.000Z';

/**
 * LegacyPlanCycle(plan_cycle) := plan_cycle.created_at < LEGACY_PLAN_CYCLE_CUTOFF.
 * Single server-side helper for planning gate bypass (Doc 14, 15, 20).
 */
export function isLegacyPlanCycle(planCycle: { created_at: string }): boolean {
  const cutoff = new Date(LEGACY_PLAN_CYCLE_CUTOFF).getTime();
  const created = new Date(planCycle.created_at).getTime();
  return created < cutoff;
}

/**
 * True iff the current caller may bypass POR gate (buckets/evaluation) for this cycle.
 * Bypass allowed ONLY when: LegacyPlanCycle(cycle) AND caller is Approver/Admin (canPerformPlanningGovernance).
 * New cycles must never bypass; normal runtime must not have skip/relax available.
 */
export async function canBypassPorGateForLegacy(planCycleId: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { data: cycle, error } = await supabase
    .from(TABLE_PLAN_CYCLES)
    .select('id, created_at, workspace_id')
    .eq('id', planCycleId)
    .single();
  if (error || !cycle) return false;
  if (!isLegacyPlanCycle(cycle)) return false;
  return canPerformPlanningGovernance((cycle as { workspace_id?: string | null }).workspace_id ?? null);
}

/**
 * Whether the current user can perform governance actions (Authorize POR, Propose replan, Close cycle).
 * Requires tenant context; optionally require cycle to belong to current workspace.
 * Used as Approver/Admin check for legacy gate bypass (Doc 14).
 */
export function canPerformPlanningGovernance(cycleWorkspaceId?: string | null): boolean {
  const tenant = getTenantContext();
  if (!tenant) return false;
  if (cycleWorkspaceId != null && cycleWorkspaceId !== '') return tenant.workspaceId === cycleWorkspaceId;
  return true;
}

// ── Planning Cycle Approver Check ───────────────────────────────────────────────
//
// The planning_cycle_roles per-cycle overlay has been removed (migration 555).
//
// Until migration 785 this function stated the rule itself — workspace
// admin/owner, nobody else — while `authorize_por` on the server enforced a
// wider one. A Priority Ratifier, the role whose whole description is
// ratifying priorities, was refused here and never reached the server that
// would have let them through, and was told the act "requires workspace admin
// or owner access", which was not the rule being enforced anywhere.
//
// So it no longer states the rule. It asks the database for the same answer
// `authorize_por` acts on, which is the only way the two cannot drift.

/**
 * Assert the current user may authorize a Plan of Record (and close the cycle)
 * for this plan cycle. Returns an error string when denied, null when allowed.
 *
 * Authority is `can_authorize_plan_of_record` (migration 785): workspace
 * admin/owner, or a role carrying `process.legitimacy_advance` or
 * `process.publish` — held by Priority Ratifier among others. Since migration
 * 843 it also checks the governing cycle's "Items / selection" decision right
 * when one is set, exactly as `authorize_por` does.
 */
export async function assertPlanningApprover(
  planCycleId: string,
): Promise<string | null> {
  const tenant = getTenantContext();
  if (!tenant?.userId) return 'Access denied: not authenticated';

  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('can_authorize_plan_of_record', {
    p_plan_cycle_id: planCycleId,
  });

  // A failed check is not a pass. Say which it was rather than guessing.
  if (error) return `Could not check your authority for this plan cycle: ${error.message}`;
  if (data === true) return null;

  return 'Access denied: closing a plan cycle or authorizing a Plan of Record takes workspace admin or owner, or a role that carries the authority to advance a plan to legitimacy (Priority Ratifier holds it) — and, when the cycle the plan was built from gives the "Items / selection" decision right to someone, it takes holding that right.';
}

/** Planning audit event (Doc 14, 17 §13). */
export interface PlanningAuditEvent {
  id: string;
  occurred_at: string;
  event_type: string;
  plan_cycle_id: string | null;
  plan_version_id: string | null;
  plan_of_record_id: string | null;
  actor_id: string | null;
  reason: string | null;
  payload: Record<string, unknown>;
}

/**
 * List audit events for a plan cycle (Governance view; Doc 17 §13).
 */
export async function getPlanningAuditEvents(planCycleId: string): Promise<{
  data: PlanningAuditEvent[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLANNING_AUDIT)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('occurred_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as PlanningAuditEvent[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load audit events' };
  }
}

/** Override audit event types (Doc 14 §5). */
export const PLANNING_OVERRIDE_EVENT_TYPES = ['CapacityOverride', 'DependencyOverride', 'ManualReadinessOverride'] as const;
export type PlanningOverrideEventType = (typeof PLANNING_OVERRIDE_EVENT_TYPES)[number];

/**
 * Record a Planning override (Doc 14 §4). Emits audit event; does not suppress future validation.
 */
export async function recordPlanningOverride(params: {
  plan_cycle_id: string;
  plan_version_id: string;
  override_type: PlanningOverrideEventType;
  justification: string;
  actor_id?: string | null;
  payload?: Record<string, unknown>;
}): Promise<{ error: string | null }> {
  try {
    const tenant = getTenantContext();
    const actorId = params.actor_id ?? tenant?.userId ?? null;
    await recordPlanningAudit({
      event_type: params.override_type,
      plan_cycle_id: params.plan_cycle_id,
      plan_version_id: params.plan_version_id,
      actor_id: actorId,
      reason: params.justification,
      payload: {
        justification: params.justification,
        impacted_object_ids: params.payload?.impacted_object_ids ?? [],
        ...params.payload,
      },
    });
    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to record override' };
  }
}

/**
 * OverrideExists(PlanVersion) — TRUE if any CapacityOverride, DependencyOverride, or ManualReadinessOverride (Doc 15 §9).
 */
export async function overrideExists(planVersionId: string): Promise<{ exists: boolean; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLANNING_AUDIT)
      .select('id')
      .eq('plan_version_id', planVersionId)
      .in('event_type', [...PLANNING_OVERRIDE_EVENT_TYPES])
      .limit(1);
    if (error) return { exists: false, error: error.message };
    return { exists: (data?.length ?? 0) > 0, error: null };
  } catch (e) {
    return { exists: false, error: e instanceof Error ? e.message : 'Failed to check overrides' };
  }
}

/**
 * List override events for a plan version (who/what/why for Governance and Capacity; Doc 17 §9, §13).
 */
export async function getOverridesForPlanVersion(planVersionId: string): Promise<{
  data: PlanningAuditEvent[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLANNING_AUDIT)
      .select('*')
      .eq('plan_version_id', planVersionId)
      .in('event_type', [...PLANNING_OVERRIDE_EVENT_TYPES])
      .order('occurred_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as PlanningAuditEvent[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to load overrides' };
  }
}


export async function getPlanVersionById(
  planVersionId: string
): Promise<{ data: PlanVersion | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('plan_versions')
      .select('*')
      .eq('id', planVersionId)
      .single();
    if (error) return { data: null, error: error.message };
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to load plan version' };
  }
}


// createInitialPlanVersionAndPor was removed: it created a draft plan version
// and authorized it as the Plan of Record in the same call, with no chance
// for the person to review the draft first — one UI click doing two
// authority transitions (build contract §2.3a treats "authorize" as its own
// governed transition, not a side effect of "create"). Its one caller
// (PlanningPlanDetail.tsx's "Create plan version" button) now calls
// createDraftPlanVersion() alone; authorizing is the separate, already-named
// "Authorize plan of record" action on the Governance page — the plan-detail
// page already told the user to go there once a version exists with no POR
// (see the "A plan version exists. Go to Governance..." banner).


export async function listInitiativesForPlanVersion(
  planVersionId: string
): Promise<{ data: Initiative[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('initiatives')
      .select('*')
      .eq('plan_version_id', planVersionId)
      .order('priority_position');
    if (error) return { data: null, error: error.message };
    return { data: (data ?? []) as Initiative[], error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Failed to list initiatives for plan version',
    };
  }
}

// ============================================================================
// Plan of Record living document — narrative context, certainty, open questions
// (migration 260, canon 10-17)
// ============================================================================

const TABLE_PLAN_OPEN_QUESTIONS = 'plan_open_questions';
const TABLE_PLANNING_AUDIT_EVENTS = 'planning_audit_events';

/** Fetch a single PlanCycle with PoR narrative context fields. */
export async function getPlanCycleWithContext(
  planCycleId: string
): Promise<{ data: PlanCycleWithContext | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_CYCLES)
      .select('*')
      .eq('id', planCycleId)
      .single();
    if (error) return { data: null, error: error.message };
    const row = data as Record<string, unknown>;
    return {
      data: {
        ...(row as PlanCycleWithContext),
        goals: Array.isArray(row.goals) ? (row.goals as string[]) : [],
        principles: Array.isArray(row.principles) ? (row.principles as string[]) : [],
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to load plan cycle' };
  }
}

/** Update the narrative context fields on a plan_cycle. Planning-owned write. */
export async function updatePlanCycleContext(
  planCycleId: string,
  context: Partial<PlanCycleContext>
): Promise<{ data: PlanCycleWithContext | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const patch: Record<string, unknown> = {};
    if ('vision_statement' in context) patch.vision_statement = context.vision_statement ?? null;
    if ('goals' in context) patch.goals = context.goals ?? [];
    if ('principles' in context) patch.principles = context.principles ?? [];
    if ('historical_context' in context) patch.historical_context = context.historical_context ?? null;
    if ('time_horizon' in context) patch.time_horizon = context.time_horizon ?? null;
    if ('org_context' in context) patch.org_context = context.org_context ?? null;

    const { data, error } = await supabase
      .from(TABLE_PLAN_CYCLES)
      .update(patch)
      .eq('id', planCycleId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    const row = data as Record<string, unknown>;
    return {
      data: {
        ...(row as PlanCycleWithContext),
        goals: Array.isArray(row.goals) ? (row.goals as string[]) : [],
        principles: Array.isArray(row.principles) ? (row.principles as string[]) : [],
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to update plan cycle context' };
  }
}

/** Fetch initiatives with certainty fields for a plan version. */
export async function listInitiativesWithCertainty(
  planVersionId: string
): Promise<{ data: InitiativeWithCertainty[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('initiatives')
      .select('*')
      .eq('plan_version_id', planVersionId)
      .order('priority_position');
    if (error) return { data: null, error: error.message };
    const rows = (data ?? []) as Record<string, unknown>[];
    return {
      data: rows.map((r) => ({
        ...(r as InitiativeWithCertainty),
        certainty_level: (r.certainty_level as CertaintyLevel | undefined) ?? 'medium',
        certainty_source: (r.certainty_source as 'derived' | 'manual' | undefined) ?? 'derived',
      })),
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to list initiatives' };
  }
}

/** Manually override the certainty lane for an initiative. Persists certainty_source = 'manual'. */
export async function upsertCertaintyOverride(
  initiativeId: string,
  certaintyLevel: CertaintyLevel
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('initiatives')
      .update({ certainty_level: certaintyLevel, certainty_source: 'manual' })
      .eq('id', initiativeId);
    if (error) return { error: error.message };
    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to update certainty override' };
  }
}

/** Reset certainty back to derived (removes manual override). */
export async function resetCertaintyToDerived(
  initiativeId: string,
  derivedLevel: CertaintyLevel
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('initiatives')
      .update({ certainty_level: derivedLevel, certainty_source: 'derived' })
      .eq('id', initiativeId);
    if (error) return { error: error.message };
    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to reset certainty' };
  }
}

// ---- Open Questions ----

/** List all open questions for a plan cycle, ordered by status then created_at. */
export async function listOpenQuestions(
  planCycleId: string
): Promise<{ data: PlanOpenQuestion[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_OPEN_QUESTIONS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('created_at');
    if (error) return { data: null, error: error.message };
    return { data: (data ?? []) as PlanOpenQuestion[], error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to load open questions' };
  }
}

/** Create a new open question for a plan cycle. */
export async function createOpenQuestion(
  planCycleId: string,
  question: string,
  createdBy?: string | null
): Promise<{ data: PlanOpenQuestion | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLAN_OPEN_QUESTIONS)
      .insert({ plan_cycle_id: planCycleId, question: question.trim(), created_by: createdBy ?? null })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanOpenQuestion, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create open question' };
  }
}

/** Update question text, status, or resolution. Sets updated_at server-side via trigger (or we patch manually). */
export async function updateOpenQuestion(
  questionId: string,
  patch: { question?: string; status?: OpenQuestionStatus; resolution?: string | null }
): Promise<{ data: PlanOpenQuestion | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const update: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.question !== undefined) update.question = patch.question.trim();
    if (patch.status !== undefined) update.status = patch.status;
    if ('resolution' in patch) update.resolution = patch.resolution ?? null;

    const { data, error } = await supabase
      .from(TABLE_PLAN_OPEN_QUESTIONS)
      .update(update)
      .eq('id', questionId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PlanOpenQuestion, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to update open question' };
  }
}

/** Delete an open question (soft-delete not needed; these are living artifacts). */
export async function deleteOpenQuestion(
  questionId: string
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from(TABLE_PLAN_OPEN_QUESTIONS)
      .delete()
      .eq('id', questionId);
    if (error) return { error: error.message };
    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to delete open question' };
  }
}

// ---- Planning audit events ----

/** Fetch recent planning audit events for a plan cycle (most recent first). */
export async function listPlanningAuditEvents(
  planCycleId: string,
  limit = 50
): Promise<{ data: PlanningAuditEvent[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_PLANNING_AUDIT_EVENTS)
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('occurred_at', { ascending: false })
      .limit(limit);
    if (error) return { data: null, error: error.message };
    return { data: (data ?? []) as PlanningAuditEvent[], error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to load audit events' };
  }
}

// ---- Divergences and tradeoffs ----

/** Fetch tradeoff decisions for a plan version (most recent first). */
export async function listTradeoffDecisions(
  planVersionId: string
): Promise<{ data: TradeoffDecision[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('tradeoff_decisions')
      .select('*')
      .eq('plan_version_id', planVersionId)
      .order('created_at', { ascending: false });
    if (error) return { data: null, error: error.message };
    return { data: (data ?? []) as TradeoffDecision[], error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to load tradeoff decisions' };
  }
}

/** Fetch all plan constraint evaluations for a plan cycle (most recent first). */
export async function listConstraintEvaluations(
  planCycleId: string
): Promise<{ data: PlanConstraintEvaluation[] | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('plan_constraint_evaluations')
      .select('*')
      .eq('plan_cycle_id', planCycleId)
      .order('evaluated_at', { ascending: false });
    if (error) return { data: null, error: error.message };
    return { data: (data ?? []) as PlanConstraintEvaluation[], error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to load constraint evaluations' };
  }
}

/**
 * Load all PoR dimensions for the detail page in a single coordinated fetch.
 * Returns the full PorDimensions object needed to render all 8 sections.
 */
export async function loadPorDimensions(
  planCycleId: string
): Promise<{ data: PorDimensions | null; error: string | null }> {
  try {
    const [cycleRes, porRes, auditRes, questionsRes, evalsRes] = await Promise.all([
      getPlanCycleWithContext(planCycleId),
      getActivePlanOfRecord(planCycleId),
      listPlanningAuditEvents(planCycleId, 100),
      listOpenQuestions(planCycleId),
      listConstraintEvaluations(planCycleId),
    ]);

    if (cycleRes.error || !cycleRes.data) {
      return { data: null, error: cycleRes.error ?? 'Plan cycle not found' };
    }

    const cycle = cycleRes.data;
    const activePor = porRes.data ?? null;
    const auditEvents = auditRes.data ?? [];
    const openQuestions = questionsRes.data ?? [];
    const evaluations = evalsRes.data ?? [];
    const latestEvaluation = evaluations[0] ?? null;

    // Load latest plan version
    const latestVersionRes = await getLatestPlanVersion(planCycleId);
    const latestVersion = latestVersionRes.data ?? null;

    let initiatives: InitiativeWithCertainty[] = [];
    let divergences: RankSequenceDivergence[] = [];
    let tradeoffs: TradeoffDecision[] = [];

    if (latestVersion) {
      const [initRes, divRes, tradeRes] = await Promise.all([
        listInitiativesWithCertainty(latestVersion.id),
        listDivergencesForPlanVersion(latestVersion.id),
        listTradeoffDecisions(latestVersion.id),
      ]);
      initiatives = initRes.data ?? [];
      divergences = divRes.data ?? [];
      tradeoffs = tradeRes.data ?? [];
    }

    const certaintyCounts = {
      high: initiatives.filter((i) => i.certainty_level === 'high').length,
      medium: initiatives.filter((i) => i.certainty_level === 'medium').length,
      low: initiatives.filter((i) => i.certainty_level === 'low').length,
    };

    return {
      data: {
        cycle,
        latestVersion,
        activePor,
        initiatives,
        openQuestions,
        latestEvaluation,
        divergences,
        tradeoffs,
        auditEvents,
        certaintyCounts,
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to load PoR dimensions' };
  }
}

/**
 * Add a milestone (a schedule anchor) to a plan version.
 *
 * The same write the public API makes: POST /v1/plan-cycles/:id/versions/:vid/milestones
 * inserts one `milestones` row with the plan version id, requiring a name and a target
 * date. There is no governed function for `milestones` (it is not in
 * GOVERNED_WRITE_MAP.md), so both surfaces insert the row directly; the table's row
 * policy keeps the write inside the caller's workspace.
 */
export async function addPlanMilestone(
  planVersionId: string,
  input: { name: string; target_date: string; success_criteria?: string | null },
): Promise<{ data: Milestone | null; error: string | null }> {
  const name = input.name.trim();
  if (!name) return { data: null, error: 'Name is required' };
  if (!input.target_date) return { data: null, error: 'Target date is required' };
  const success = input.success_criteria?.trim();
  const { data, error } = await getSupabaseClient()
    .from('milestones')
    .insert({
      plan_version_id: planVersionId,
      name,
      target_date: input.target_date,
      success_criteria: success ? success : null,
    })
    .select()
    .single();
  if (error || !data) return { data: null, error: error?.message ?? 'The milestone was not saved' };
  return { data: data as Milestone, error: null };
}
